/* eslint-disable @typescript-eslint/no-explicit-any */
﻿import { Injectable, inject } from '@angular/core';
import { AuthService } from 'app/core/auth/auth.service';
import { RoleList } from 'app/core/enums';
import {
    RbacPage,
    RbacPageName,
} from 'app/core/rbac/rbac-page-names.const';
import { RbacPermissionService } from 'app/core/rbac/rbac-permission.service';
import { Constants } from 'app/core/constant/app-constant';
import { AdminFilmService } from 'app/shared/services/admin-film.service';
import { FilmMakerService } from 'app/shared/services/film-maker.service';
import { FilmMakerWalletService } from 'app/shared/services/film-maker-wallet.service';
import { ReferAndEarnDialogService } from 'app/shared/services/refer-and-earn-dialog.service';
import { ReportService } from 'app/shared/services/report.service';
import { AuditLogService } from 'app/shared/services/audit-log.service';
import { Observable, catchError, finalize, forkJoin, map, of, switchMap, throwError } from 'rxjs';
import {
    clarifyTitleChip,
    rankFilmMatches,
    resolveFilmPick,
} from './slate-film.resolve';
import { normalizePhoneDigits } from './slate-user.parse';
import {
    SLATE_ROUTE_GUIDES,
    resolveGuideRoute,
    resolveGuideQueryParams,
} from './slate-route-guide';
import {
    SLATE_REFUSE_SUGGESTIONS_ADMIN,
    SLATE_REFUSE_SUGGESTIONS_FILMMAKER,
} from './slate-scope.guard';
import { withHelpChips, withSupportEscapeChips } from './slate-support.pure';
import {
    selectFilmAnalyticsKpis,
    selectFilmStatsKpis,
    selectUserAnalyticsKpis,
    selectWalletKpis,
    selectWebSeriesAnalyticsKpis,
    shouldShowTopFilmsTable,
} from './slate-metric.select';
import { SlateSessionService } from './slate-session.service';
import { SlatePrefsService } from './slate-prefs.service';
import {
    SlateIntentId,
    SlateEntities,
    SlateParseResult,
    SlateResponseCard,
    SlateKpiItem,
    SlateUserListType,
} from './slate.types';

@Injectable({ providedIn: 'root' })
export class SlateActionExecutor {
    private readonly _auth = inject(AuthService);
    private readonly _rbac = inject(RbacPermissionService);
    private readonly _adminFilm = inject(AdminFilmService);
    private readonly _filmMaker = inject(FilmMakerService);
    private readonly _wallet = inject(FilmMakerWalletService);
    private readonly _report = inject(ReportService);
    private readonly _referral = inject(ReferAndEarnDialogService);
    private readonly _audit = inject(AuditLogService);
    private readonly _session = inject(SlateSessionService);
    private readonly _prefs = inject(SlatePrefsService);

    execute(parsed: SlateParseResult): Observable<SlateResponseCard[]> {
        const audience = this.isFilmmaker() ? 'filmmaker' : 'admin';
        const chips =
            audience === 'filmmaker'
                ? SLATE_REFUSE_SUGGESTIONS_FILMMAKER
                : SLATE_REFUSE_SUGGESTIONS_ADMIN;

        if (parsed.kind === 'out_of_scope') {
            return of([
                {
                    type: 'refuse',
                    title: 'Out of scope',
                    body:
                        parsed.message ||
                        'Talkie helps with Studio — films, series, wallet, reports, and how-to. If something looks wrong or unclear, tap Help and support.',
                    chips: withSupportEscapeChips(chips),
                    links: this.supportLinks(),
                },
            ]);
        }

        if (parsed.kind === 'out_of_role') {
            return of([
                {
                    type: 'refuse',
                    title: 'Access restricted',
                    body: parsed.message,
                    chips: withSupportEscapeChips(chips),
                    links: [
                        ...(audience === 'admin'
                            ? [
                                  {
                                      label: 'Open Access Control',
                                      route: '/admin/settings/access-control',
                                  },
                              ]
                            : [
                                  {
                                      label: 'Open My Dashboard',
                                      route: '/film-maker/dashboard',
                                  },
                              ]),
                        ...this.supportLinks(),
                    ],
                },
            ]);
        }

        if (parsed.kind === 'clarify') {
            if (parsed.promptChips?.length) {
                return of([
                    {
                        type: 'clarify',
                        title: parsed.clarifyTitle || 'What should I do?',
                        body:
                            (parsed.message ||
                                'Please tap an option below, or type a short English question.') +
                            ' If Talkie still looks wrong, use Help and support.',
                        chips: withSupportEscapeChips(parsed.promptChips),
                        links: this.supportLinks(),
                    },
                ]);
            }
            if (parsed.candidates?.length) {
                return of([
                    {
                        type: 'clarify',
                        title: 'Which one did you mean?',
                        body: 'Tap a match so Talkie can fetch the right live data. Need a person? Use Help and support.',
                        chips: withSupportEscapeChips(
                            parsed.candidates.map((c) => c.label)
                        ),
                        links: this.supportLinks(),
                    },
                ]);
            }
        }

        const intentId = parsed.intentId;
        if (!intentId) {
            return of([
                {
                    type: 'error',
                    title: 'Something went wrong',
                    body: 'No intent to run. Try again, or contact support.',
                    chips: withSupportEscapeChips(chips),
                    links: this.supportLinks(),
                },
            ]);
        }

        this._prefs.track('intent', intentId);

        return this.runIntent(intentId, parsed).pipe(
            map((cards) => {
                if (
                    parsed.kind === 'intent' ||
                    parsed.kind === 'follow_up'
                ) {
                    this._session.setContext({
                        lastIntentId: intentId,
                        lastEntities: parsed.entities,
                        lastRbacPage: parsed.rbacPage,
                        lastLabel: parsed.label,
                    });
                }
                return this.decorateCards(cards, parsed);
            }),
            catchError((err) => {
                this._prefs.track('intent_error', intentId);
                const status = err?.status;
                if (status === 403) {
                    return of([
                        {
                            type: 'refuse' as const,
                            title: 'Access denied',
                            body: 'You do not have permission for that Studio data. Contact support if that seems wrong.',
                            chips: withSupportEscapeChips(chips),
                            links: this.supportLinks(),
                        },
                    ]);
                }
                return of([
                    {
                        type: 'error' as const,
                        title: 'Could not load live data',
                        body:
                            (err?.error?.message ||
                                err?.message ||
                                'Please try again in a moment.') +
                            ' If numbers still look wrong, connect with Help and support.',
                        chips: withSupportEscapeChips(chips),
                        links: this.supportLinks(),
                    },
                ]);
            })
        );
    }

    private decorateCards(
        cards: Array<SlateResponseCard>,
        parsed: SlateParseResult
    ): Array<SlateResponseCard> {
        const detail = this._prefs.getDetailMode();
        return cards.map((c) => {
            const next: SlateResponseCard = {
                ...c,
                chips:
                    c.type === 'error' ||
                    c.type === 'clarify' ||
                    c.type === 'refuse'
                        ? withSupportEscapeChips(c.chips)
                        : withHelpChips(c.chips),
            };
            if (detail === 'short' && c.shortBody) {
                next.body = c.shortBody;
            }
            if (
                c.type === 'table' &&
                (!c.rows || c.rows.length === 0) &&
                !c.body
            ) {
                next.body =
                    'No rows for this filter. Try another date, upload content, or ask Help and support if you expected data.';
                next.chips = withSupportEscapeChips([
                    ...(c.chips || []),
                    this.isFilmmaker()
                        ? 'How do I upload a film?'
                        : 'List films from last 30 days',
                ]);
            }
            if (c.type === 'kpi' && c.kpis?.length) {
                const nums = c.kpis
                    .map((k) => Number(k.value))
                    .filter((n) => Number.isFinite(n) && n >= 0);
                if (nums.length >= 2) {
                    const max = Math.max(...nums, 1);
                    next.spark = nums
                        .slice(0, 8)
                        .map((n) => Math.round((n / max) * 100));
                }
            }
            if (
                parsed.message &&
                c.type === 'guide' &&
                parsed.intentId === 'help_support'
            ) {
                next.body = parsed.message + ' ' + (c.body || '');
            }
            return next;
        });
    }

    /** Map clarify chip label back to an intent id suggestion text */
    clarifyPrompt(label: string): string {
        const map: Record<string, string> = {
            'Film analytics': 'Film views last 7 days',
            'Web series analytics': 'Web series analytics this week',
            'User analytics': 'User growth last 30 days',
            'Film statistics': 'How many films pending?',
            'Film list': 'List films from last 30 days',
            'Web series list': 'List web series',
            'User list': 'List filmmakers',
            'Views report': 'Views report last month',
            'Watch activity': 'Watch activity last week',
            'Purchase report': 'Content purchase report last 30 days',
            'Wallet & level': 'Tell me my current wallet balance',
            'Referral code': 'What is my referral code?',
            'Help & support': 'Help and support',
            'Studio help': 'How do I upload a film?',
            // Metric chips — short; follow-up merges last date range
            'Show revenue': 'Show revenue',
            'Show views': 'Show views',
            'Show my revenue': 'Show my revenue',
            'Show filmmaker revenue': 'Show filmmaker revenue',
            'Show box office revenue': 'Show box office revenue',
            'Show purchases': 'Show purchases',
            'Show all metrics': 'Show all metrics',
            'Show top films': 'Show top films',
            'Show unique viewers': 'Show unique viewers',
            'Show unique views': 'Show unique views',
            'Show completed views': 'Show completed views',
            'Show incomplete views': 'Show incomplete views',
            'Show watch time': 'Show watch time',
            'Show full revenue': 'Show revenue',
            'Top 5 films': 'Top 5 films',
            'Top 5 seasons': 'Top 5 seasons',
            'Show filmmaker revenue only': 'Show filmmaker revenue',
            'Show box office revenue only': 'Show box office revenue',
            'Show new users': 'Show new users',
            'Show active users': 'Show active users',
            'Show cash balance': 'Show cash balance',
            'Show reel balance': 'Show reel balance',
            'Show level': 'Show my level',
            'How many films approved?': 'How many films approved?',
            'Retry last ask': 'Help and support',
            'Data looks wrong — Help and support': 'Help and support',
            'I do not understand — Help and support': 'Help and support',
            'How to approve a film?': 'How to approve a film?',
            'How to reject a film?': 'How to reject a film?',
            'How to start film conversion?': 'How to start film conversion?',
            'Which movies are ready for approve?':
                'Which movies are ready for approve?',
            'List submitted films': 'List submitted films',
        };
        return map[label] || label;
    }

    private runIntent(
        intentId: SlateIntentId,
        parsed: SlateParseResult
    ): Observable<SlateResponseCard[]> {
        const e = parsed.entities;
        switch (intentId) {
            case 'route_guide':
                return of(this.guideCards(parsed));
            case 'film_analytics':
                return this.filmAnalytics(e);
            case 'webseries_analytics':
                return this.webseriesAnalytics(e);
            case 'user_analytics':
                return this.userAnalytics(e);
            case 'film_stats':
                return this.filmStats(e);
            case 'film_list':
                return this.filmList(e);
            case 'webseries_list':
                return this.webseriesList(e);
            case 'user_list':
                return this.userList(e);
            case 'views_report':
                return this.viewsReport(e);
            case 'watch_activity':
                return this.watchActivity(e);
            case 'purchase_report':
                return this.purchaseReport(e);
            case 'wallet_balance':
                return this.walletBalance(e);
            case 'referral_code':
                return this.referralCode();
            case 'help_support':
                return of(this.helpSupportCards(parsed.message));
            case 'my_access':
                return of(this.myAccessCards());
            case 'pending_queue':
                return this.pendingQueue(e);
            case 'film_moderation':
                return this.filmModeration(e);
            case 'audit_recent':
                return this.auditRecent();
            case 'open_content':
                return this.openContent(e);
            case 'film_lookup':
                return this.filmLookup(e);
            case 'user_lookup':
                return this.userLookup(e);
            default:
                return of([
                    {
                        type: 'error',
                        title: 'Unsupported',
                        body: 'That action is not wired yet.',
                    },
                ]);
        }
    }

    private isFilmmaker(): boolean {
        return this._auth.currentUserRole === RoleList.FilmMaker;
    }

    /**
     * Temporarily set RBAC pageName for Studio API call, then restore.
     * Prevents Slate from permanently overriding the page the user is on
     * (e.g. Dashboard → Film-Dashboard), which can skew later requests.
     */
    private withPageContext<T>(
        pageName: RbacPageName | undefined,
        call: () => Observable<T>
    ): Observable<T> {
        const previous = this._rbac.getCurrentPageName() as RbacPageName | null;
        if (pageName) {
            this._rbac.setCurrentPage(pageName);
        }
        return call().pipe(
            finalize(() => {
                if (previous) {
                    this._rbac.setCurrentPage(previous);
                }
            })
        );
    }

    private filterSummary(e: SlateEntities, apiRange?: { startDate?: string; endDate?: string }): string {
        const bits: string[] = [];
        if (apiRange?.startDate && apiRange?.endDate) {
            bits.push(`${apiRange.startDate} – ${apiRange.endDate}`);
        } else if (e.dateRange) {
            bits.push(e.dateRange.label);
        }
        bits.push(`Top ${e.limit}`);
        if (e.topCriteria) bits.push(e.topCriteria);
        if (e.status) bits.push(e.status);
        if (e.filmTitle) bits.push(`Film: ${e.filmTitle}`);
        else if (e.filmTitleQuery) bits.push(`Film search: ${e.filmTitleQuery}`);
        else if (e.matchParams) bits.push(`Search: ${e.matchParams}`);
        return bits.join(' · ');
    }

    private formatRangeLabel(start?: string, end?: string): string | null {
        if (!start || !end) return null;
        const a = new Date(start);
        const b = new Date(end);
        if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) {
            return `${start} – ${end}`;
        }
        const fmt = (d: Date) =>
            d.toLocaleDateString('en-GB', {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
            });
        return `${fmt(a)} – ${fmt(b)}`;
    }

    private guideCards(parsed: SlateParseResult): SlateResponseCard[] {
        const audience = this.isFilmmaker() ? 'filmmaker' : 'admin';
        const guide =
            SLATE_ROUTE_GUIDES.find((g) => g.id === parsed.guideId) ||
            SLATE_ROUTE_GUIDES.find((g) => g.id === parsed.entities.guideId) ||
            SLATE_ROUTE_GUIDES.find((g) => g.id === 'general_help')!;

        if (
            audience === 'admin' &&
            guide.rbacPage &&
            this._rbac.isLoaded() &&
            !this._rbac.can(guide.rbacPage, 'read') &&
            guide.id !== 'rbac_help' &&
            guide.id !== 'general_help'
        ) {
            return [
                {
                    type: 'refuse',
                    title: 'Access restricted',
                    body: `You do not have access to “${guide.title}”.`,
                    chips: SLATE_REFUSE_SUGGESTIONS_ADMIN,
                    links: [
                        {
                            label: 'Permissions help',
                            route: '/admin/settings/access-control',
                        },
                    ],
                },
            ];
        }

        const route = resolveGuideRoute(guide, audience);
        const baseQp = resolveGuideQueryParams(guide) || {};
        const ctxFilmId = this._session.context.lastEntities?.filmId;
        const queryParams =
            guide.id === 'approve_reject_film' && ctxFilmId
                ? { ...baseQp, filmId: ctxFilmId }
                : Object.keys(baseQp).length
                  ? baseQp
                  : undefined;
        const links: NonNullable<SlateResponseCard['links']> = [
            { label: guide.linkLabel, route, queryParams },
        ];
        if (guide.id === 'approve_reject_film' && ctxFilmId) {
            const title =
                this._session.context.lastEntities?.filmTitle || 'film';
            links.unshift({
                label: `Open film ${title}`,
                route: `/admin/box-office/films/view/${ctxFilmId}`,
            });
        }
        return [
            {
                type: 'guide',
                title: guide.title,
                steps: guide.steps,
                links,
                chips: withHelpChips(guide.relatedPrompts),
            },
        ];
    }

    private filmAnalytics(e: SlateEntities): Observable<SlateResponseCard[]> {
        return this.resolveFilmEntities(e).pipe(
            switchMap((resolved) => {
                if (resolved.clarify) {
                    return of(resolved.clarify);
                }
                Object.assign(e, resolved.entities);
                if (e.compareDateRange) {
                    const a = { ...resolved.entities };
                    const b = {
                        ...resolved.entities,
                        dateRange: e.compareDateRange,
                        compareDateRange: undefined as typeof e.compareDateRange | undefined,
                    };
                    return forkJoin([
                        this.fetchFilmAnalytics(a),
                        this.fetchFilmAnalytics(b),
                    ]).pipe(
                        map(([cardsA, cardsB]) => {
                            const tagged = (cards: SlateResponseCard[], tag: string) =>
                                cards.map((c) => ({
                                    ...c,
                                    title: c.title
                                        ? `${c.title} · ${tag}`
                                        : tag,
                                    filterSummary: tag,
                                }));
                            return [
                                ...tagged(
                                    cardsA,
                                    a.dateRange?.label || 'Period A'
                                ),
                                ...tagged(
                                    cardsB,
                                    b.dateRange?.label || 'Period B'
                                ),
                            ];
                        })
                    );
                }
                return this.fetchFilmAnalytics(resolved.entities);
            })
        );
    }

    private fetchFilmAnalytics(
        e: SlateEntities
    ): Observable<SlateResponseCard[]> {
        const start = e.dateRange?.startDate;
        const end = e.dateRange?.endDate;
        const groupBy = e.groupBy || 'day';
        const topCriteria = e.topCriteria || 'views';
        const limit = e.limit;
        const filmId = e.filmId || '';

        if (this.isFilmmaker()) {
            const uid = this._auth.currentUserValue?._id || '';
            return this._filmMaker
                .getFilmAnalyticsDashboard(
                    uid,
                    filmId,
                    groupBy,
                    start,
                    end,
                    topCriteria,
                    limit
                )
                .pipe(map((res) => this.mapFilmAnalytics(res, e, true)));
        }

        return this.withPageContext(RbacPage.DASHBOARD, () =>
            this._adminFilm.getFilmAnalyticsDashboard(
                undefined,
                filmId || undefined,
                groupBy,
                start,
                end,
                topCriteria,
                limit
            )
        ).pipe(map((res) => this.mapFilmAnalytics(res, e, false)));
    }

    /** Resolve film title query → filmId via simple catalog search */
    private resolveFilmEntities(e: SlateEntities): Observable<{
        entities: SlateEntities;
        clarify?: SlateResponseCard[];
    }> {
        if (e.filmId) {
            return of({ entities: e });
        }
        const query = (e.filmTitleQuery || '').trim();
        if (!query) {
            return of({ entities: e });
        }

        return this.searchFilmsByTitle(query).pipe(
            map((films) => {
                const ranked = rankFilmMatches(query, films);
                const pick = resolveFilmPick(ranked);
                if (pick.kind === 'none') {
                    return {
                        entities: e,
                        clarify: [
                            {
                                type: 'clarify' as const,
                                title: 'Film not found',
                                body: `No film matched “${query}”. Try quotes, e.g. views for "Exact Title".`,
                                chips: [
                                    'Film views last 7 days',
                                    'List films from last 30 days',
                                ],
                            },
                        ],
                    };
                }
                if (pick.kind === 'ambiguous') {
                    return {
                        entities: e,
                        clarify: [
                            {
                                type: 'clarify' as const,
                                title: 'Which film?',
                                body: `Multiple titles match “${query}”. Pick one:`,
                                chips: pick.films.map((f) =>
                                    clarifyTitleChip(f, 'Analytics for')
                                ),
                            },
                        ],
                    };
                }
                return {
                    entities: {
                        ...e,
                        filmId: pick.film.id,
                        filmTitle: pick.film.title,
                        filmTitleQuery: query,
                    },
                };
            })
        );
    }

    private searchFilmsByTitle(
        query: string
    ): Observable<
        Array<{ _id: string; title: string; status?: string }>
    > {
        if (this.isFilmmaker()) {
            // Scoped catalog only — never global simple list
            return this._filmMaker
                .getFilmList({
                    page: 1,
                    limit: 50,
                    matchParams: query,
                })
                .pipe(
                    map((res: any) => {
                        const rows = Array.isArray(res?.data)
                            ? res.data
                            : Array.isArray(res?.data?.films)
                              ? res.data.films
                              : [];
                        return rows.map((f: any) => ({
                            _id: f._id || f.id,
                            title: f.title || f.filmName || '',
                            status: f.status || f.approveStatus,
                        }));
                    }),
                    catchError(() => of([]))
                );
        }
        return this.withPageContext(RbacPage.FILM_LIST, () =>
            this._adminFilm.getSimpleFilmList(1, 50, undefined, query)
        ).pipe(
            map((res: any) => {
                const rows = Array.isArray(res?.data?.films)
                    ? res.data.films
                    : Array.isArray(res?.data)
                      ? res.data
                      : [];
                return rows.map((f: any) => ({
                    _id: f._id || f.id,
                    title: f.title || '',
                    status: f.status || f.approveStatus,
                }));
            }),
            catchError(() => of([]))
        );
    }

    private notInYourCatalogCard(
        kind: 'film' | 'webseries' | 'unknown',
        title: string
    ): SlateResponseCard {
        const kindLabel = kind === 'webseries' ? 'web series' : 'film';
        const noun = kind === 'webseries' ? 'series' : 'movies';
        return {
            type: 'clarify',
            title: this.isFilmmaker() ? 'Not in your catalog' : 'Title not found',
            body: this.isFilmmaker()
                ? `You have not added a ${kindLabel} named “${title}”. Ask Talkie only shows your own ${noun}. Wrong? Use Help and support.`
                : `I could not find a ${kindLabel} matching “${title}”. Check the spelling, or browse the list. Need a person? Help and support.`,
            chips: this.isFilmmaker()
                ? kind === 'webseries'
                    ? ['Show my series', 'How do I upload a film?', 'Help and support']
                    : ['Show my films', 'How do I upload a film?', 'Help and support']
                : kind === 'webseries'
                  ? ['List web series', 'Open film in view mode', 'Help and support']
                  : ['List films from last 30 days', 'Open series in view mode', 'Help and support'],
        };
    }

    private openChipLabel(
        kind: 'film' | 'webseries' | 'unknown',
        mode: 'view' | 'edit',
        film: { id: string; title: string; status?: string }
    ): { label: string; prompt: string } {
        const noun = kind === 'webseries' ? 'series' : 'film';
        const st = (film.status || '').trim();
        const statusBit = st ? ` · ${st}` : '';
        // Humans see title + status; prompt keeps id for exact open
        return {
            label: `Open "${film.title}"${statusBit}`,
            prompt: `Open ${noun} (id:${film.id}) "${film.title}" in ${mode} mode`,
        };
    }

    private mapFilmAnalytics(
        res: any,
        e: SlateEntities,
        filmmaker: boolean
    ): SlateResponseCard[] {
        const data = res?.data || {};
        const films = Array.isArray(data.films)
            ? data.films.slice(0, e.limit)
            : [];
        const apiRangeLabel = this.formatRangeLabel(
            data.range?.startDate,
            data.range?.endDate
        );
        const selected = selectFilmAnalyticsKpis(data, e, { filmmaker });
        const filterBits = [
            apiRangeLabel || e.dateRange?.label,
            e.filmTitle ? `film: ${e.filmTitle}` : null,
            e.metricFocus && e.metricFocus !== 'summary'
                ? `focus: ${e.metricFocus.replace(/_/g, ' ')}`
                : null,
            shouldShowTopFilmsTable(e) ? `Top ${e.limit}` : null,
        ].filter(Boolean);

        const cards: SlateResponseCard[] = [
            {
                type: 'kpi',
                title: selected.title,
                live: true,
                filterSummary: filterBits.join(' · '),
                body: selected.body,
                kpis: selected.kpis,
                chips: selected.chips,
                links: [
                    {
                        label: 'Open dashboard',
                        route: filmmaker
                            ? '/film-maker/dashboard'
                            : '/admin/dashboard',
                    },
                    ...(filmmaker
                        ? []
                        : [
                              {
                                  label: 'Open views report',
                                  route: '/admin/reports/views-report',
                              },
                          ]),
                ],
            },
        ];

        if (shouldShowTopFilmsTable(e) && films.length) {
            cards.push({
                type: 'table',
                title:
                    films.length >= e.limit
                        ? `Top ${e.limit} films`
                        : `Top films (${films.length})`,
                          live: true,
                          columns: [
                              { key: 'title', label: 'Title' },
                              { key: 'totalViews', label: 'Views' },
                              { key: 'uniqueViews', label: 'Unique' },
                    { key: 'minutesWatched', label: 'Minutes' },
                          ],
                          rows: films.map((f: any) => ({
                              title: f.title || 'Untitled',
                              totalViews: f.totalViews ?? 0,
                              uniqueViews: f.uniqueViews ?? 0,
                              minutesWatched: f.minutesWatched ?? 0,
                          })),
            });
        }

        return cards;
    }

    private webseriesAnalytics(
        e: SlateEntities
    ): Observable<SlateResponseCard[]> {
        const start = e.dateRange?.startDate;
        const end = e.dateRange?.endDate;
        const groupBy = e.groupBy || 'day';

        if (this.isFilmmaker()) {
            const uid = this._auth.currentUserValue?._id || '';
            return this._filmMaker
                .getWebSeriesAnalyticsDashboard(
                    uid,
                    '',
                    groupBy,
                    start,
                    end
                )
                .pipe(map((res) => this.mapWsAnalytics(res, e, true)));
        }

        return this.withPageContext(RbacPage.DASHBOARD, () =>
            this._adminFilm.getWebSeriesAnalyticsDashboard(
                undefined,
                undefined,
                groupBy,
                start,
                end
            )
        ).pipe(map((res) => this.mapWsAnalytics(res, e, false)));
    }

    private mapWsAnalytics(
        res: any,
        e: SlateEntities,
        filmmaker: boolean
    ): SlateResponseCard[] {
        const data = res?.data || {};
        const seasons = (
            Array.isArray(data.seasons) ? data.seasons : []
        ).slice(0, e.limit);
        const selected = selectWebSeriesAnalyticsKpis(data, e, { filmmaker });
        const apiRangeLabel = this.formatRangeLabel(
            data.range?.startDate,
            data.range?.endDate
        );
        const filterBits = [
            apiRangeLabel || e.dateRange?.label,
            e.metricFocus && e.metricFocus !== 'summary'
                ? `focus: ${e.metricFocus.replace(/_/g, ' ')}`
                : null,
            shouldShowTopFilmsTable(e) ? `Top ${e.limit}` : null,
        ].filter(Boolean);

        const cards: SlateResponseCard[] = [
            {
                type: 'kpi',
                title: selected.title,
                live: true,
                filterSummary: filterBits.join(' · '),
                body: selected.body,
                kpis: selected.kpis,
                chips: selected.chips,
                links: [
                    {
                        label: 'Open dashboard',
                        route: filmmaker
                            ? '/film-maker/dashboard'
                            : '/admin/dashboard',
                    },
                ],
            },
        ];

        if (shouldShowTopFilmsTable(e) && seasons.length) {
            cards.push({
                type: 'table',
                title:
                    seasons.length >= e.limit
                        ? `Top ${e.limit} seasons`
                        : `Top seasons (${seasons.length})`,
                          live: true,
                          columns: [
                              { key: 'title', label: 'Season' },
                              { key: 'totalViews', label: 'Views' },
                          ],
                          rows: seasons.map((s: any) => ({
                              title:
                                  s.title ||
                                  s.seasonName ||
                                  s.name ||
                                  'Season',
                              totalViews: s.totalViews ?? 0,
                          })),
            });
        }

        return cards;
    }

    private userAnalytics(e: SlateEntities): Observable<SlateResponseCard[]> {
        const gb =
            e.groupBy === 'week'
                ? 'WEEK'
                : e.groupBy === 'month'
                  ? 'MONTH'
                  : 'DAY';
        return this.withPageContext(RbacPage.DASHBOARD, () =>
            this._adminFilm.getUserAnalyticsDashboard(
                e.dateRange?.startDate,
                e.dateRange?.endDate,
                gb
            )
        ).pipe(
                map((res: any) => {
                    const data = res?.data || {};
                const selected = selectUserAnalyticsKpis(data, e);
                    return [
                        {
                            type: 'kpi' as const,
                        title: selected.title,
                            live: true,
                        filterSummary: this.filterSummary(e, data.range),
                        body: selected.body,
                        kpis: selected.kpis,
                        chips: selected.chips,
                            links: [
                                {
                                    label: 'Open dashboard',
                                    route: '/admin/dashboard',
                                },
                            ],
                        },
                    ];
                })
            );
    }

    private filmStats(e: SlateEntities): Observable<SlateResponseCard[]> {
        if (this.isFilmmaker()) {
            return this._filmMaker.getFilmStatistics().pipe(
                map((res: any) => this.mapFilmStats(res, e, true))
            );
        }
        return this.withPageContext(RbacPage.FILM_LIST, () =>
            this._adminFilm.getFilmStatistics()
        ).pipe(map((res: any) => this.mapFilmStats(res, e, false)));
    }

    private mapFilmStats(
        res: any,
        e: SlateEntities,
        filmmaker: boolean
    ): SlateResponseCard[] {
        const data = res?.data || res || {};
        const selected = selectFilmStatsKpis(data, e, filmmaker);
        return [
            {
                type: 'kpi',
                title: selected.title,
                live: true,
                body: selected.body,
                kpis: selected.kpis,
                chips: selected.chips,
                links: [
                    {
                        label: filmmaker ? 'Open My Films' : 'Open Films',
                        route: filmmaker
                            ? '/film-maker/my-films'
                            : '/admin/box-office/films',
                    },
                ],
            },
        ];
    }

    private filmList(e: SlateEntities): Observable<SlateResponseCard[]> {
        const limit = e.limit;
        const statusWanted = (e.status || '').toLowerCase();
        const isApprovalQueue =
            statusWanted === 'submitted' ||
            statusWanted === 'in review' ||
            statusWanted === 'pending';
        const mapRows = (
            res: any,
            title: string,
            route: string,
            extraCol?: { key: string; label: string }
        ) => {
            let rows = (res?.data || []) as any[];
            if (statusWanted) {
                rows = rows.filter(
                    (f) =>
                        String(f.status || '').toLowerCase() === statusWanted
                );
            }
            rows = rows.slice(0, limit);
            const openChips = rows.slice(0, 5).map((f: any) =>
                this.openChipLabel('film', 'view', {
                    id: String(f._id || f.id || ''),
                    title: f.title || 'Untitled',
                    status: f.status,
                })
            );
            const actionChips = isApprovalQueue
                ? [
                      ...openChips,
                      'How to approve a film?',
                      'How to start film conversion?',
                      'How to reject a film?',
                  ]
                : openChips.length
                  ? [...openChips.slice(0, 3), 'How to approve a film?']
                  : ['How to approve a film?', 'Films in review'];
            const columns = [
                { key: 'title', label: 'Title' },
                { key: 'status', label: 'Status' },
                ...(extraCol ? [extraCol] : []),
            ];
            return [
                {
                    type: 'table' as const,
                    title: isApprovalQueue
                        ? 'Films ready for approval'
                        : title,
                    live: true,
                    filterSummary: this.filterSummary(e),
                    shortBody: isApprovalQueue
                        ? 'Submitted titles — open a row to Approve / Decline / Convert.'
                        : undefined,
                    body: isApprovalQueue
                        ? rows.length
                            ? 'Showing Submitted films only. Approve needs conversion COMPLETE. Talkie cannot approve from chat — tap Open below, then use Studio buttons.'
                            : `No films found with status ${e.status}.`
                        : statusWanted && !rows.length
                          ? `No films found with status ${e.status}.`
                          : undefined,
                    columns,
                    rows: rows.map((f: any) => ({
                        title: f.title || 'Untitled',
                        status: f.status || '—',
                        ...(extraCol
                            ? {
                                  [extraCol.key]: f.createdAt
                                      ? String(f.createdAt).slice(0, 10)
                                      : '—',
                              }
                            : {}),
                    })),
                    chips: withHelpChips(actionChips),
                    links: [
                        { label: 'View all', route },
                        ...(isApprovalQueue
                            ? [
                                  {
                                      label: 'How to approve',
                                      route,
                                  },
                              ]
                            : []),
                    ],
                },
            ];
        };

        if (this.isFilmmaker()) {
            return this._filmMaker
                .getFilmList({
                    page: 1,
                    limit: Math.max(limit, statusWanted ? 50 : limit),
                    matchParams: e.matchParams || '',
                    status: e.status,
                })
                .pipe(
                    map((res: any) =>
                        mapRows(res, 'Your films', '/film-maker/my-films')
                    )
                );
        }

        return this.withPageContext(RbacPage.FILM_LIST, () =>
            this._adminFilm.getFilmList({
                page: 1,
                limit: Math.max(limit, statusWanted ? 50 : limit),
                matchParams: e.matchParams || '',
                status: e.status,
                dateFrom: e.dateRange?.startDate,
                dateTo: e.dateRange?.endDate,
            })
        ).pipe(
            map((res: any) =>
                mapRows(res, 'Films', '/admin/box-office/films', {
                    key: 'createdAt',
                    label: 'Created',
                })
            )
        );
    }

    private webseriesList(e: SlateEntities): Observable<SlateResponseCard[]> {
        const limit = e.limit;
        const statusWanted = (e.status || '').toLowerCase();
        const mapRows = (res: any, title: string, route: string) => {
            let rows = (res?.data || []) as any[];
            if (statusWanted) {
                rows = rows.filter((f) => {
                    const st = String(
                        f.latestSeasonStatus || f.status || ''
                    ).toLowerCase();
                    return st === statusWanted;
                });
            }
            rows = rows.slice(0, limit);
                        return [
                            {
                                type: 'table' as const,
                    title,
                                live: true,
                                filterSummary: this.filterSummary(e),
                    body:
                        statusWanted && !rows.length
                            ? `No web series found with status ${e.status}.`
                            : undefined,
                                columns: [
                                    { key: 'title', label: 'Title' },
                                    { key: 'status', label: 'Status' },
                                ],
                                rows: rows.map((f: any) => ({
                                    title: f.title || 'Untitled',
                        status: f.latestSeasonStatus || f.status || '—',
                    })),
                    links: [{ label: 'View all', route }],
                    chips: statusWanted
                        ? [
                              'List my all web series',
                              'Show my films',
                              'Help and support',
                          ]
                        : [
                              'Show my all web series with status Approved',
                              'Show my films',
                                ],
                            },
                        ];
        };

        if (this.isFilmmaker()) {
            return this._filmMaker
                .getWebseriesList({
                    page: 1,
                    limit: Math.max(limit, statusWanted ? 50 : limit),
                    matchParams: e.matchParams || '',
                    status: e.status,
                })
                .pipe(
                    map((res: any) =>
                        mapRows(res, 'Your web series', '/film-maker/my-series')
                    )
                );
        }

        return this.withPageContext(RbacPage.WEB_SERIES_LIST, () =>
            this._adminFilm.getWebSeriesList({
                page: 1,
                limit: Math.max(limit, statusWanted ? 50 : limit),
                matchParams: e.matchParams || '',
                status: e.status,
                dateFrom: e.dateRange?.startDate,
                dateTo: e.dateRange?.endDate,
            })
        ).pipe(
            map((res: any) =>
                mapRows(res, 'Web series', '/admin/box-office/webseries')
            )
            );
    }

    private userList(e: SlateEntities): Observable<SlateResponseCard[]> {
        const type: SlateUserListType = e.userListType || 'FILM_MAKER';
        const params = new URLSearchParams({
            page: '1',
            limit: String(e.limit),
            sortOrder: 'desc',
            key: 'createdAt',
            matchParams: e.matchParams || '',
        });
        if (e.dateRange?.startDate)
            params.set('startDate', e.dateRange.startDate);
        if (e.dateRange?.endDate) params.set('endDate', e.dateRange.endDate);

        const meta = this.userTypeMeta(type);
        if (
            this._rbac.isLoaded() &&
            !this._rbac.can(meta.page, 'read')
        ) {
            return of([
                {
                    type: 'refuse',
                    title: 'Access unavailable',
                    body: `You do not currently have access to the ${meta.label} list. If that seems unexpected, please contact a Super Admin so they can review your role permissions.`,
                    chips: [
                        'List filmmakers',
                        'How do I open the dashboard?',
                    ],
                },
            ]);
        }

        return this.withPageContext(meta.page, () =>
            meta.fetch(params)
        ).pipe(
            map((res: any) => {
                const rows = (res?.data || []).slice(0, e.limit);
                return [
                    {
                        type: 'table' as const,
                        title: `${meta.label} list`,
                        live: true,
                        filterSummary: this.filterSummary(e),
                        columns: [
                            { key: 'name', label: 'Name' },
                            { key: 'email', label: 'Email' },
                            { key: 'phone', label: 'Phone' },
                        ],
                        rows: rows.map((u: any) => ({
                            name: this.formatUserDisplayName(u),
                            email: u.email || '—',
                            phone: this.formatUserPhone(u),
                        })),
                        links: [{ label: 'View all', route: meta.listRoute }],
                        chips: rows.slice(0, 3).map((u: any) => {
                            const id = u._id || u.id;
                            const name = this.formatUserDisplayName(u);
                            return `Open ${meta.chipNoun} "${name}"`;
                        }),
                    },
                ];
            }),
            catchError(() =>
                of([
                    {
                        type: 'error' as const,
                        title: 'Could not load list',
                        body: 'Studio could not load that user list right now. Try again, or open the page from the menu.',
                        links: [
                            { label: 'Open list', route: meta.listRoute },
                        ],
                    },
                ])
            )
        );
    }

    private userTypeMeta(type: SlateUserListType): {
        page: RbacPageName;
        label: string;
        listRoute: string;
        chipNoun: string;
        fetch: (params: URLSearchParams) => Observable<any>;
        profileRoute: (id: string, mode: 'view' | 'edit') => {
            route: string;
            queryParams?: Record<string, string>;
        };
    } {
        switch (type) {
            case 'VIEWER':
                return {
                    page: RbacPage.VIEWERS,
                    label: 'Viewer',
                    listRoute: '/admin/users/viewers',
                    chipNoun: 'viewer',
                    fetch: (p) => this._adminFilm.getViewerList(p),
                    profileRoute: (id, mode) => ({
                        route: `/admin/users/viewers/${id}`,
                        queryParams: { isEdit: mode === 'edit' ? 'true' : 'false' },
                    }),
                };
            case 'ADMIN':
                return {
                    page: RbacPage.ADMINS,
                    label: 'Admin',
                    listRoute: '/admin/users/admin-list',
                    chipNoun: 'admin',
                    fetch: (p) => this._adminFilm.getAdminList(p),
                    profileRoute: (id, mode) => ({
                        route: `/admin/users/create-user/${id}`,
                        queryParams: { mode: mode === 'edit' ? 'edit' : 'view' },
                    }),
                };
            case 'AGENT':
                return {
                    page: RbacPage.CHANNEL_PARTNER,
                    label: 'Channel partner',
                    listRoute: '/admin/users/channel-partners',
                    chipNoun: 'channel partner',
                    fetch: (p) => this._adminFilm.getChannelPartnerList(p),
                    profileRoute: (id, mode) => ({
                        route: `/admin/users/channel-partners/${id}`,
                        queryParams: { isEdit: mode === 'edit' ? 'true' : 'false' },
                    }),
                };
            case 'CONTENT_CURATOR':
                return {
                    page: RbacPage.CONTENT_CURATOR,
                    label: 'Content curator',
                    listRoute: '/admin/users/content-curators',
                    chipNoun: 'content curator',
                    fetch: (p) => this._adminFilm.getContentCuratorList(p),
                    profileRoute: (id, mode) => ({
                        route: `/admin/users/content-curators/${id}`,
                        queryParams: { isEdit: mode === 'edit' ? 'true' : 'false' },
                    }),
                };
            case 'FILM_MAKER':
            default:
                return {
                    page: RbacPage.FILMMAKERS,
                    label: 'Filmmaker',
                    listRoute: '/admin/users/film-makers',
                    chipNoun: 'filmmaker',
                    fetch: (p) => this._adminFilm.getFilmMakerList(p),
                    profileRoute: (id, mode) => ({
                        route: `/admin/users/film-makers/${id}`,
                        queryParams: { isEdit: mode === 'edit' ? 'true' : 'false' },
                    }),
                };
        }
    }

    private formatUserDisplayName(u: any): string {
        return (
            u.fullName ||
            u.name ||
            [u.firstname || u.firstName, u.lastname || u.lastName]
                .filter(Boolean)
                .join(' ') ||
            '—'
        );
    }

    private formatUserPhone(u: any): string {
        return this.formatPhoneDisplay(
            u.phoneNumber || u.phone || '',
            u.countryCodeNumeric || u.countryCode || ''
        );
    }

    /** Avoid "+91 +9199…" — strip duplicate country code from phone field. */
    private formatPhoneDisplay(phoneRaw: any, ccRaw: any): string {
        let phone = String(phoneRaw || '').trim();
        if (!phone) return '—';
        let cc = String(ccRaw || '')
            .trim()
            .replace(/^\+/, '')
            .replace(/\D/g, '');

        let digits = phone.replace(/\D/g, '');
        if (!digits) return '—';

        if (cc && digits.startsWith(cc) && digits.length > cc.length + 7) {
            digits = digits.slice(cc.length);
        } else if (!cc && digits.length >= 12 && digits.startsWith('91')) {
            cc = '91';
            digits = digits.slice(2);
        }

        if (!digits) return '—';
        return cc ? `+${cc} ${digits}` : digits;
    }

    private viewsReport(e: SlateEntities): Observable<SlateResponseCard[]> {
        const params = new URLSearchParams({
            page: '1',
            limit: String(e.limit),
            sortOrder: 'desc',
            key: 'createdAt',
            matchParams: e.matchParams || '',
        });
        if (e.dateRange?.startDate)
            params.set('startDate', e.dateRange.startDate);
        if (e.dateRange?.endDate) params.set('endDate', e.dateRange.endDate);

        return this.withPageContext(RbacPage.VIEWS_REPORT, () =>
            this._report.getViewsReport(params)
        ).pipe(
            map((res: any) => {
                const rows = (res?.data || []).slice(0, e.limit);
                return [
                    {
                        type: 'table' as const,
                        title: 'Views report',
                        live: true,
                        filterSummary: this.filterSummary(e),
                        columns: [
                            { key: 'title', label: 'Title' },
                            { key: 'views', label: 'Views' },
                        ],
                        rows: rows.map((r: any) => ({
                            title: r.title || r.filmTitle || '—',
                            views:
                                r.views ??
                                r.totalViews ??
                                r.count ??
                                0,
                        })),
                        links: [
                            {
                                label: 'Open Views Report',
                                route: '/admin/reports/views-report',
                            },
                        ],
                    },
                ];
            })
        );
    }

    private watchActivity(e: SlateEntities): Observable<SlateResponseCard[]> {
        const params = new URLSearchParams({
            page: '1',
            limit: String(e.limit),
            sortOrder: 'desc',
            key: 'createdAt',
            matchParams: e.matchParams || '',
        });
        if (e.dateRange?.startDate)
            params.set('startDate', e.dateRange.startDate);
        if (e.dateRange?.endDate) params.set('endDate', e.dateRange.endDate);

        return this.withPageContext(RbacPage.WATCH_ACTIVITY_REPORT, () =>
            this._report.getWatchActivityReport(params)
        ).pipe(
            map((res: any) => {
                const data = res?.data;
                const rows = (
                    Array.isArray(data)
                        ? data
                        : data?.items || data?.records || []
                ).slice(0, e.limit);
                return [
                    {
                        type: 'table' as const,
                        title: 'Watch activity',
                        live: true,
                        filterSummary: this.filterSummary(e),
                        columns: [
                            { key: 'title', label: 'Content' },
                            { key: 'user', label: 'User' },
                            { key: 'progress', label: 'Progress' },
                        ],
                        rows: rows.map((r: any) => ({
                            title:
                                r.title ||
                                r.contentTitle ||
                                r.filmTitle ||
                                '—',
                            user:
                                r.userName ||
                                r.email ||
                                r.userId ||
                                '—',
                            progress:
                                r.progress ??
                                r.watchPercentage ??
                                r.status ??
                                '—',
                        })),
                        links: [
                            {
                                label: 'Open Watch Activity',
                                route: '/admin/reports/watch-activity-report',
                            },
                        ],
                    },
                ];
            })
        );
    }

    private purchaseReport(e: SlateEntities): Observable<SlateResponseCard[]> {
        const params = new URLSearchParams({
            page: '1',
            limit: String(e.limit),
            sortOrder: 'desc',
            key: 'createdAt',
            matchParams: e.matchParams || '',
        });
        if (e.dateRange?.startDate)
            params.set('startDate', e.dateRange.startDate);
        if (e.dateRange?.endDate) params.set('endDate', e.dateRange.endDate);

        return this.withPageContext(RbacPage.PURCHASE_REPORT, () =>
            this._report.getFilmPurchaseSummary(params)
        ).pipe(
            map((res: any) => {
                const rows = (res?.data || []).slice(0, e.limit);
                return [
                    {
                        type: 'table' as const,
                        title: 'Content purchase report',
                        live: true,
                        filterSummary: this.filterSummary(e),
                        columns: (() => {
                            const focus = e.metricFocus;
                            if (focus === 'revenue' || focus === 'filmmaker_revenue' || focus === 'boxoffice_revenue') {
                                return [
                                    { key: 'title', label: 'Title' },
                                    { key: 'amount', label: 'Amount' },
                                ];
                            }
                            if (focus === 'purchases') {
                                return [
                                    { key: 'title', label: 'Title' },
                                    { key: 'purchases', label: 'Purchases' },
                                ];
                            }
                            return [
                            { key: 'title', label: 'Title' },
                            { key: 'purchases', label: 'Purchases' },
                            { key: 'amount', label: 'Amount' },
                            ];
                        })(),
                        rows: rows.map((r: any) => ({
                            title: r.title || r.filmTitle || '—',
                            purchases:
                                r.purchaseCount ??
                                r.totalPurchases ??
                                r.count ??
                                0,
                            amount:
                                r.totalAmount ??
                                r.amount ??
                                r.revenue ??
                                0,
                        })),
                        links: [
                            {
                                label: 'Open Purchase Report',
                                route: '/admin/reports/film-purchase-report',
                            },
                        ],
                    },
                ];
            })
        );
    }

    private walletBalance(e: SlateEntities = {} as SlateEntities): Observable<SlateResponseCard[]> {
        return this._wallet.getWalletBalance().pipe(
            map((res: any) => {
                const data = res?.data || {};
                const focus =
                    e.metricFocus === 'summary' || !e.metricFocus
                        ? ({ ...e, wantAllMetrics: true, metricFocus: 'all' } as SlateEntities)
                        : e;
                const selected = selectWalletKpis(data, focus);
                return [
                    {
                        type: 'kpi' as const,
                        title: selected.title,
                        live: true,
                        body: selected.body,
                        kpis: selected.kpis,
                        chips: selected.chips,
                        links: [
                            {
                                label: 'Open transactions',
                                route: '/film-maker/transaction-history',
                            },
                        ],
                    },
                ];
            })
        );
    }

    private referralCode(): Observable<SlateResponseCard[]> {
        if (!this.isFilmmaker()) {
            return of([
                {
                    type: 'refuse',
                    title: 'Filmmaker referral',
                    body: 'Referral codes are available on the filmmaker portal. Admins can open Referral settings instead.',
                    chips: ['Help and support'],
                    links: [
                        {
                            label: 'Referral settings',
                            route: '/admin/settings/rewards',
                            queryParams: { tab: 'referral-event-settings' },
                        },
                    ],
                },
            ]);
        }
        return this._referral.getUserReferralCode('IN').pipe(
            map((res: any) => {
                const code =
                    res?.data?.referralCode ||
                    res?.data?.code ||
                    res?.referralCode ||
                    '—';
                return [
                    {
                        type: 'kpi' as const,
                        title: 'Your referral code',
                        live: true,
                        body: 'Share this code so others can join with your referral.',
                        kpis: [
                            {
                                label: 'Referral code',
                                value: code,
                                copyable: code !== '—',
                            },
                        ],
                        chips: [
                            'Tell me my current wallet balance',
                            'Help and support',
                        ],
                        links: [
                            {
                                label: 'Open Referral & Sharing',
                                route: '/film-maker/referral-sharing',
                            },
                            ...(code !== '—'
                                ? [
                                      {
                                          label: 'Copy referral code',
                                          route: '/film-maker/referral-sharing',
                                          copyText: String(code),
                                      },
                                  ]
                                : []),
                        ],
                    },
                ];
            }),
            catchError(() =>
                of([
                    {
                        type: 'error' as const,
                        title: 'Could not load referral code',
                        body: 'Please open Referral & Sharing, or contact support.',
                        chips: ['Help and support'],
                        links: [
                            {
                                label: 'Open Referral & Sharing',
                                route: '/film-maker/referral-sharing',
                            },
                            ...this.supportLinks(),
                        ],
                    },
                ])
            )
        );
    }

    private helpSupportCards(extraMessage?: string): Array<SlateResponseCard> {
        return [
            {
                type: 'guide',
                title: 'Help & support',
                shortBody: 'Email, WhatsApp, or Call support below.',
                body:
                    (extraMessage ? extraMessage + ' ' : '') +
                    'Need a person? Use Email, WhatsApp, or Call below. Talkie can still help with Studio questions in simple English. If live numbers look wrong, share the exact ask + date with support.',
                steps: [
                    `Email: ${Constants.BRAND_SUPPORT_EMAIL}`,
                    `Call: ${Constants.BRAND_PHONE}`,
                    'WhatsApp: tap WhatsApp to message support.',
                ],
                links: this.supportLinks(),
                chips: withHelpChips([
                    'How do I upload a film?',
                    'Show my films',
                    'Film views last 7 days',
                    'What can I access?',
                ]),
            },
        ];
    }

    private myAccessCards(): Array<SlateResponseCard> {
        if (this.isFilmmaker()) {
            return [
                {
                    type: 'guide',
                    title: 'Your filmmaker access',
                    body: 'Filmmaker Studio covers your films, series, wallet, referral, and uploads. Admin user directory is not available.',
                    chips: withHelpChips([
                        'Show my films',
                        'Wallet balance',
                        'Help and support',
                    ]),
                    links: [
                        {
                            label: 'Open My Dashboard',
                            route: '/film-maker/dashboard',
                        },
                        ...this.supportLinks(),
                    ],
                },
            ];
        }
        if (!this._rbac.isLoaded()) {
            return [
                {
                    type: 'clarify',
                    title: 'Permissions still loading',
                    body: 'Try again in a moment, or open Access Control.',
                    chips: withHelpChips(['What can I access?']),
                    links: [
                        {
                            label: 'Open Access Control',
                            route: '/admin/settings/access-control',
                        },
                        ...this.supportLinks(),
                    ],
                },
            ];
        }
        const sample: Array<{ page: RbacPageName; label: string }> = [
            { page: RbacPage.DASHBOARD, label: 'Dashboard / analytics' },
            { page: RbacPage.FILM_LIST, label: 'Films list' },
            { page: RbacPage.WEB_SERIES_LIST, label: 'Web series list' },
            { page: RbacPage.VIEWS_REPORT, label: 'Views report' },
            { page: RbacPage.PURCHASE_REPORT, label: 'Purchase report' },
            {
                page: RbacPage.ACCESS_CONTROL,
                label: 'Access control',
            },
        ];
        const rows = sample.map((s) => ({
            area: s.label,
            read: this._rbac.can(s.page, 'read') ? 'Yes' : 'No',
            update: this._rbac.can(s.page, 'update') ? 'Yes' : 'No',
        }));
        return [
            {
                type: 'table',
                title: 'Your Studio access (sample)',
                live: true,
                shortBody: 'Read/update flags for key pages.',
                body: 'Based on your current RBAC role. Missing access? Ask a Super Admin, or Help and support.',
                columns: [
                    { key: 'area', label: 'Area' },
                    { key: 'read', label: 'Read' },
                    { key: 'update', label: 'Update' },
                ],
                rows,
                chips: withHelpChips([
                    'Where is access control?',
                    'Help and support',
                ]),
                links: [
                    {
                        label: 'Open Access Control',
                        route: '/admin/settings/access-control',
                    },
                    ...this.supportLinks(),
                ],
            },
        ];
    }

    private filmModeration(e: SlateEntities): Observable<SlateResponseCard[]> {
        const action = e.moderationAction || 'approve_advice';
        const guideId =
            action === 'convert_advice'
                ? 'start_film_conversion'
                : 'approve_reject_film';

        const title = (e.filmTitleQuery || e.filmTitle || '').trim();
        if (!title) {
            return of(
                this.guideCards({
                    kind: 'intent',
                    intentId: 'route_guide',
                    label:
                        action === 'convert_advice'
                            ? 'Start film conversion'
                            : 'Approve or reject a film',
                    entities: { ...e, guideId },
                    thinking: [],
                    guideId,
                })
            );
        }

        return this.searchFilmsByTitle(title).pipe(
            switchMap((films) => {
                const ranked = rankFilmMatches(title, films);
                const pick = resolveFilmPick(ranked);
                if (pick.kind === 'none') {
                    return of([
                        {
                            type: 'clarify' as const,
                            title: 'Film not found',
                            body: `No film matched “${title}”. Try quotes, or ask Which movies are ready for approve?`,
                            chips: withHelpChips([
                                'Which movies are ready for approve?',
                                'List submitted films',
                                'How to approve a film?',
                            ]),
                            links: [
                                {
                                    label: 'Open Films list',
                                    route: '/admin/box-office/films',
                                },
                                ...this.supportLinks(),
                            ],
                        },
                    ]);
                }
                if (pick.kind === 'ambiguous') {
                    return of([
                        {
                            type: 'clarify' as const,
                            title: 'Which film?',
                            body: `Several titles match “${title}”. Pick one to open for Approve / Decline / Convert:`,
                            chips: withHelpChips(
                                pick.films.map((f) =>
                                    this.openChipLabel('film', 'view', f)
                                )
                            ),
                            links: [
                                {
                                    label: 'Open Films list',
                                    route: '/admin/box-office/films',
                                },
                            ],
                        },
                    ]);
                }

                const id = pick.film.id;
                const filmTitle = pick.film.title;
                const listStatus = pick.film.status;
                const detail$: Observable<any> = this.isFilmmaker()
                    ? this._filmMaker.getFilmById(id)
                    : this.withPageContext(RbacPage.FILM_LIST, () =>
                          this._adminFilm.getFilmById(id)
                      );

                return detail$.pipe(
                    map((res: any) => {
                        const film = res?.data || res || {};
                        const status =
                            film.status ||
                            film.approveStatus ||
                            listStatus ||
                            '—';
                        const contact = this.formatFilmmakerContact({
                            ...film,
                            title: filmTitle,
                        });
                        const openView = this.openChipLabel('film', 'view', {
                            id,
                            title: filmTitle,
                            status:
                                status !== '—' ? String(status) : undefined,
                        });
                        const verb =
                            action === 'convert_advice'
                                ? 'Convert'
                                : action === 'reject_advice'
                                  ? 'Decline'
                                  : 'Approve';
                        const steps =
                            action === 'convert_advice'
                                ? [
                                      `Open “${filmTitle}” in view mode.`,
                                      'Use Convert on the film / Media section (needs Convert permission).',
                                      'Wait until conversion is COMPLETE before Approve shows.',
                                      'Talkie cannot start conversion from chat — Studio confirm dialog required.',
                                  ]
                                : action === 'reject_advice'
                                  ? [
                                        `Open “${filmTitle}” (status: ${status}).`,
                                        'Use Decline on the film row or detail page (RBAC).',
                                        'Talkie cannot decline from chat.',
                                    ]
                                  : [
                                        `Open “${filmTitle}” (status: ${status}).`,
                                        'Approve needs conversion COMPLETE and status not already Approved/Declined.',
                                        'Use Approve on the film (set release date in the dialog).',
                                        'Talkie cannot approve from chat — tap Open below.',
                                    ];

                        return [
                            {
                                type: 'guide' as const,
                                title: `${verb} “${filmTitle}”`,
                                live: true,
                                shortBody: `Open film → use ${verb} in Studio.`,
                                body: `Current status: ${status}. Talkie shows the path only — ${verb} stays in Studio with your RBAC checks.`,
                                steps,
                                kpis: [
                                    { label: 'Title', value: filmTitle },
                                    { label: 'Status', value: status },
                                    {
                                        label: 'Filmmaker',
                                        value: contact.name,
                                        copyable: contact.name !== '—',
                                    },
                                    {
                                        label: 'Filmmaker email',
                                        value: contact.email,
                                        copyable: contact.email !== '—',
                                    },
                                    {
                                        label: 'Filmmaker mobile',
                                        value: contact.phone,
                                        copyable: contact.phone !== '—',
                                    },
                                ],
                                chips: withHelpChips([
                                    openView,
                                    'Which movies are ready for approve?',
                                    'How to approve a film?',
                                    'How to start film conversion?',
                                    'How to reject a film?',
                                ]),
                                links: [
                                    {
                                        label: `Open ${filmTitle}`,
                                        route: `/admin/box-office/films/view/${id}`,
                                    },
                                    {
                                        label: 'Open Films list',
                                        route: '/admin/box-office/films',
                                    },
                                    ...this.supportLinks(),
                                ],
                            },
                        ];
                    }),
                    catchError(() =>
                        of([
                            {
                                type: 'guide' as const,
                                title: `Open “${filmTitle}”`,
                                body: 'Could not load live film details. Open the film in Studio, or Help and support.',
                                chips: withHelpChips([
                                    this.openChipLabel('film', 'view', {
                                        id,
                                        title: filmTitle,
                                        status: listStatus,
                                    }),
                                    'Which movies are ready for approve?',
                                ]),
                                links: [
                                    {
                                        label: `Open ${filmTitle}`,
                                        route: `/admin/box-office/films/view/${id}`,
                                    },
                                    ...this.supportLinks(),
                                ],
                            },
                        ])
                    )
                );
            })
        );
    }

    private pendingQueue(e: SlateEntities): Observable<SlateResponseCard[]> {
        if (this.isFilmmaker()) {
            return this._filmMaker.getFilmStatistics().pipe(
                map((res: any) => {
                    const d = res?.data || res || {};
                    const kpis: SlateKpiItem[] = [
                        {
                            label: 'Total',
                            value: d.totalFilms ?? d.total ?? '—',
                        },
                        {
                            label: 'Draft',
                            value: d.draft ?? d.drafts ?? '—',
                        },
                        {
                            label: 'Submitted',
                            value: d.submitted ?? '—',
                        },
                        {
                            label: 'In review',
                            value: d.inReview ?? d.in_review ?? '—',
                        },
                        {
                            label: 'Approved',
                            value: d.approved ?? '—',
                        },
                    ];
                    return [
                        {
                            type: 'kpi' as const,
                            title: 'Your film queue',
                            live: true,
                            shortBody: 'Counts from your catalog.',
                            body: 'Live counts for your films only. Open My Films to act.',
                            kpis,
                            chips: withHelpChips([
                                'Show my films',
                                'Show my drafts',
                                'How do I upload a film?',
                            ]),
                            links: [
                                {
                                    label: 'Open My Films',
                                    route: '/film-maker/my-films',
                                },
                            ],
                        },
                    ];
                }),
                catchError(() =>
                    of([
                        {
                            type: 'error' as const,
                            title: 'Could not load queue',
                            body: 'Open My Films, or contact Help and support.',
                            chips: withHelpChips(['Show my films']),
                            links: this.supportLinks(),
                        },
                    ])
                )
            );
        }
        return this.withPageContext(RbacPage.FILM_LIST, () =>
            this._adminFilm.getFilmStatistics()
        ).pipe(
            map((res: any) => {
                const d = res?.data || {};
                const kpis: SlateKpiItem[] = [
                    {
                        label: 'Total',
                        value: d.totalFilms ?? d.total ?? d.count ?? '—',
                    },
                    {
                        label: 'Draft',
                        value: d.draft ?? d.drafts ?? '—',
                    },
                    {
                        label: 'Submitted',
                        value: d.submitted ?? '—',
                    },
                    {
                        label: 'In review',
                        value: d.inReview ?? d.in_review ?? '—',
                    },
                    {
                        label: 'Approved',
                        value: d.approved ?? '—',
                    },
                ];
                return [
                    {
                        type: 'kpi' as const,
                        title: 'Film approval queue',
                        live: true,
                        body: 'Live Studio counts. Use list filters for each status. To approve/reject, open the film in review.',
                        kpis,
                        chips: withHelpChips([
                            'List draft films',
                            'How to approve a film?',
                            'Films in review',
                        ]),
                        links: [
                            {
                                label: 'Open Films list',
                                route: '/admin/box-office/films',
                            },
                        ],
                    },
                ];
            }),
            catchError(() =>
                of([
                    {
                        type: 'error' as const,
                        title: 'Could not load queue',
                        body: 'Open Films list, or Help and support.',
                        chips: withHelpChips(['List films from last 30 days']),
                        links: this.supportLinks(),
                    },
                ])
            )
        );
    }

    private auditRecent(): Observable<SlateResponseCard[]> {
        if (this.isFilmmaker()) {
            return of([
                {
                    type: 'refuse',
                    title: 'Admin only',
                    body: 'Audit trail is available to Studio admins. Use Help and support if you need a person.',
                    chips: withSupportEscapeChips([
                        'Show my films',
                        'Help and support',
                    ]),
                    links: this.supportLinks(),
                },
            ]);
        }
        return this.withPageContext(RbacPage.AUDIT_LOG, () =>
            this._audit.getAuditLogs({ page: 1, limit: 8 })
        ).pipe(
            map((res: any) => {
                const rows = Array.isArray(res?.data)
                    ? res.data
                    : Array.isArray(res?.data?.logs)
                      ? res.data.logs
                      : Array.isArray(res?.logs)
                        ? res.logs
                        : [];
                if (!rows.length) {
                    return [
                        {
                            type: 'clarify' as const,
                            title: 'No recent audit rows',
                            body: 'Open Audit Logs to filter by actor or date. Wrong empty? Help and support.',
                            chips: withSupportEscapeChips([
                                'Where is audit log?',
                            ]),
                            links: [
                                {
                                    label: 'Open Audit Logs',
                                    route: '/admin/audit-logs',
                                },
                                ...this.supportLinks(),
                            ],
                        },
                    ];
                }
                return [
                    {
                        type: 'table' as const,
                        title: 'Recent audit activity',
                        live: true,
                        shortBody: 'Latest Studio changes.',
                        body: 'Live from Audit Logs. Open the page for full filters.',
                        columns: [
                            { key: 'when', label: 'When' },
                            { key: 'actor', label: 'Actor' },
                            { key: 'action', label: 'Action' },
                            { key: 'resource', label: 'Resource' },
                        ],
                        rows: rows.slice(0, 8).map((r: any) => ({
                            when:
                                r.createdAt ||
                                r.timestamp ||
                                r.date ||
                                '—',
                            actor:
                                r.actorName ||
                                r.actor?.name ||
                                r.actorEmail ||
                                r.actorId ||
                                '—',
                            action: r.action || r.event || '—',
                            resource:
                                r.resourceType ||
                                r.resource?.type ||
                                r.entityType ||
                                '—',
                        })),
                        chips: withHelpChips([
                            'Where is audit log?',
                            'Films in review',
                        ]),
                        links: [
                            {
                                label: 'Open Audit Logs',
                                route: '/admin/audit-logs',
                            },
                        ],
                    },
                ];
            }),
            catchError(() =>
                of([
                    {
                        type: 'error' as const,
                        title: 'Could not load audit logs',
                        body: 'Open Audit Logs, or Help and support.',
                        chips: withSupportEscapeChips([
                            'Where is audit log?',
                        ]),
                        links: [
                            {
                                label: 'Open Audit Logs',
                                route: '/admin/audit-logs',
                            },
                            ...this.supportLinks(),
                        ],
                    },
                ])
            )
        );
    }

    private supportLinks(): NonNullable<SlateResponseCard['links']> {
        const phoneDigits = Constants.BRAND_PHONE.replace(/\D/g, '');
        return [
            {
                label: 'Email support',
                route: `mailto:${Constants.BRAND_SUPPORT_EMAIL}`,
            },
            {
                label: 'WhatsApp',
                route: `https://wa.me/${phoneDigits}`,
            },
            {
                label: 'Call support',
                route: `tel:${phoneDigits}`,
            },
        ];
    }

    /** Account status only — never confuse with role (e.g. DEVELOPER). */
    private formatUserAccountStatus(user: any): string {
        const raw =
            user?.userApproveStatus ??
            user?.approveStatus ??
            user?.accountStatus ??
            user?.userStatus ??
            (typeof user?.status === 'string' &&
            !/^(admin|developer|viewer|filmmaker|film_maker|agent|curator|partner)$/i.test(
                user.status
            )
                ? user.status
                : null) ??
            (user?.isActive === false
                ? 'Inactive'
                : user?.isActive === true
                  ? 'Active'
                  : null) ??
            (user?.isBlocked === true ? 'Blocked' : null);

        if (raw == null || raw === '') return '';
        const s = String(raw).trim();
        if (!s || /^(admin|developer|viewer|filmmaker|film_maker|agent|curator|partner)$/i.test(s)) {
            return '';
        }
        // Pretty Title Case for API enums
        if (/^[A-Z_]+$/.test(s)) {
            return s
                .toLowerCase()
                .split('_')
                .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                .join(' ');
        }
        return s;
    }

    private filmLookup(e: SlateEntities): Observable<SlateResponseCard[]> {
        const title = (e.filmTitleQuery || e.filmTitle || '').trim();
        const focuses = e.detailFocus?.length
            ? e.detailFocus
            : (['summary'] as NonNullable<SlateEntities['detailFocus']>);
        const prefer = e.contentKind || 'unknown';

        if (!title) {
            return of([
                {
                    type: 'clarify',
                    title: 'Which title?',
                    body: 'Ask with a film or series name, for example: Who is the filmmaker of "R patel"?',
                    chips: [
                        'List films from last 30 days',
                        'List web series',
                    ],
                },
            ]);
        }

        const canFilm =
            this.isFilmmaker() ||
            !this._rbac.isLoaded() ||
            this._rbac.can(RbacPage.FILM_LIST, 'read');
        const canSeries =
            this.isFilmmaker() ||
            !this._rbac.isLoaded() ||
            this._rbac.can(RbacPage.WEB_SERIES_LIST, 'read');

        if (!canFilm && !canSeries) {
            return of([
                {
                    type: 'refuse',
                    title: 'Access unavailable',
                    body: 'You do not currently have access to film or web series details in Studio. If that seems unexpected, please contact a Super Admin so they can review your role permissions.',
                    chips: ['How do I open the dashboard?'],
                    links: [
                        {
                            label: 'Open Access Control',
                            route: '/admin/settings/access-control',
                        },
                    ],
                },
            ]);
        }

        const search$ =
            prefer === 'webseries' && canSeries
                ? this.searchSeriesRich(title).pipe(
                      map((rows) => ({
                          kind: 'webseries' as const,
                          rows,
                      }))
                  )
                : prefer === 'film' && canFilm
                  ? this.searchFilmsRich(title).pipe(
                        map((rows) => ({ kind: 'film' as const, rows }))
                    )
                  : canFilm
                    ? this.searchFilmsRich(title).pipe(
                          switchMap((films) => {
                              if (films.length || !canSeries) {
                                  return of({
                                      kind: 'film' as const,
                                      rows: films,
                                  });
                              }
                              return this.searchSeriesRich(title).pipe(
                                  map((rows) => ({
                                      kind: 'webseries' as const,
                                      rows,
                                  }))
                              );
                          })
                      )
                    : this.searchSeriesRich(title).pipe(
                          map((rows) => ({
                              kind: 'webseries' as const,
                              rows,
                          }))
                      );

        return search$.pipe(
            switchMap(({ kind, rows }) => {
                const ranked = rankFilmMatches(title, rows);
                const pick = resolveFilmPick(ranked);
                const kindLabel =
                    kind === 'webseries' ? 'web series' : 'film';

                if (pick.kind === 'none') {
                    return of([
                        this.notInYourCatalogCard(kind, title),
                    ]);
                }

                if (pick.kind === 'ambiguous') {
                    return of([
                        {
                            type: 'clarify' as const,
                            title: 'Which title?',
                            body: this.isFilmmaker()
                                ? `Several of your ${kindLabel} titles match “${title}”. Pick one:`
                                : `Several ${kindLabel} titles match “${title}”. Pick one:`,
                            chips: pick.films.map((f) => {
                                const focusHint =
                                    focuses[0] === 'summary'
                                        ? 'details'
                                        : focuses[0];
                                const noun =
                                    kind === 'webseries'
                                        ? 'series'
                                        : 'film';
                                const st = (f.status || '').trim();
                                const suffix = st ? ` · ${st}` : '';
                                if (focusHint === 'filmmaker') {
                                    return `Who is the filmmaker of "${f.title}" ${noun}?${suffix}`;
                                }
                                if (focusHint === 'status') {
                                    return `What is the status of "${f.title}" ${noun}?${suffix}`;
                                }
                                if (focusHint === 'purchases') {
                                    return `How many purchases for "${f.title}" ${noun}?${suffix}`;
                                }
                                return `Tell me about "${f.title}" ${noun}${suffix}`;
                            }),
                        },
                    ]);
                }

                const full =
                    rows.find(
                        (r) => String(r._id || r.id) === pick.film.id
                    ) || {};
                const base: Record<string, any> = {
                    ...full,
                    _id: pick.film.id,
                    title: pick.film.title,
                };

                const needFm =
                    focuses.includes('filmmaker') ||
                    focuses.includes('summary');

                if (kind === 'film' && needFm) {
                    const detail$: Observable<any> = this.isFilmmaker()
                        ? this._filmMaker.getFilmById(pick.film.id)
                        : this.withPageContext(RbacPage.FILM_LIST, () =>
                              this._adminFilm.getFilmById(pick.film.id)
                          );
                    return detail$.pipe(
                        map((res: any): SlateResponseCard[] => {
                            const data = res?.data || {};
                            return [
                                this.buildFilmDetailCard(
                                    {
                                        ...base,
                                        ...data,
                                        filmMaker:
                                            data.filmMaker ||
                                            base['filmMaker'],
                                        filmMakerId:
                                            data.filmMakerId ||
                                            data.filmMaker?._id ||
                                            base['filmMakerId'],
                                    },
                                    focuses,
                                    'film'
                                ),
                            ];
                        }),
                        catchError(
                            (): Observable<SlateResponseCard[]> =>
                                of([
                                    this.buildFilmDetailCard(
                                        base,
                                        focuses,
                                        'film'
                                    ),
                                ])
                        )
                    );
                }

                if (
                    kind === 'webseries' &&
                    needFm &&
                    !this.isFilmmaker()
                ) {
                    return this.withPageContext(
                        RbacPage.WEB_SERIES_LIST,
                        () =>
                            this._adminFilm.getWebSeriesById(pick.film.id)
                    ).pipe(
                        map((res: any): SlateResponseCard[] => {
                            const data = res?.data || {};
                            return [
                                this.buildFilmDetailCard(
                                    {
                                        ...base,
                                        ...data,
                                        filmMaker:
                                            data.filmMaker ||
                                            base['filmMaker'],
                                    },
                                    focuses,
                                    'webseries'
                                ),
                            ];
                        }),
                        catchError(
                            (): Observable<SlateResponseCard[]> =>
                                of([
                                    this.buildFilmDetailCard(
                                        base,
                                        focuses,
                                        'webseries'
                                    ),
                                ])
                        )
                    );
                }

                return of([
                    this.buildFilmDetailCard(base, focuses, kind),
                ]);
            }),
            catchError((err) => {
                if (err?.status === 403 || err?.status === 401) {
                    return of([
                        {
                            type: 'refuse' as const,
                            title: 'Access unavailable',
                            body: 'Studio denied access to that film or series data. Your role may be missing permission — ask a Super Admin, or use Help and support.',
                            chips: [
                                'Help and support',
                                'How do I open the dashboard?',
                            ],
                            links: [
                                {
                                    label: 'Open Access Control',
                                    route: '/admin/settings/access-control',
                                },
                                ...this.supportLinks(),
                            ],
                        },
                    ]);
                }
                return of([
                    {
                        type: 'error' as const,
                        title: 'Could not load title',
                        body: 'Please try again in a moment.',
                        chips: ['Help and support'],
                    },
                ]);
            })
        );
    }

    private userLookup(e: SlateEntities): Observable<SlateResponseCard[]> {
        if (this.isFilmmaker()) {
            return of([
                {
                    type: 'refuse',
                    title: 'Admin feature',
                    body: 'User search is available to Studio admins. You can still ask about your films, series, and wallet.',
                    chips: [
                        'Show my films',
                        'How do I upload a film?',
                    ],
                },
            ]);
        }

        const query = (e.userQuery || e.matchParams || '').trim();
        const queryKind = e.userQueryKind || 'name';
        const open = !!e.userOpen;
        const openMode = e.openMode || 'view';

        if (!query) {
            return of([
                {
                    type: 'clarify',
                    title: 'Which user?',
                    body: 'Share a mobile number, email, or name — for example: Who is mobile 9876543210?',
                    chips: [
                        'List viewers',
                        'List filmmakers',
                        'List channel partners',
                    ],
                },
            ]);
        }

        const types: SlateUserListType[] = e.userListType
            ? [e.userListType]
            : [
                  'VIEWER',
                  'FILM_MAKER',
                  'ADMIN',
                  'AGENT',
                  'CONTENT_CURATOR',
              ];

        const searchable = types.filter((t) => {
            const meta = this.userTypeMeta(t);
            return (
                !this._rbac.isLoaded() ||
                this._rbac.can(meta.page, 'read')
            );
        });

        if (!searchable.length) {
            return of([
                {
                    type: 'refuse',
                    title: 'Access unavailable',
                    body: 'You do not currently have access to user directories in Studio. If that seems unexpected, please contact a Super Admin so they can review your role permissions.',
                    chips: ['How do I open the dashboard?'],
                },
            ]);
        }

        const calls = searchable.map((t) => {
            const meta = this.userTypeMeta(t);
            const params = new URLSearchParams({
                page: '1',
                limit: '25',
                sortOrder: 'desc',
                key: 'createdAt',
                matchParams: query,
            });
            return this.withPageContext(meta.page, () =>
                meta.fetch(params)
            ).pipe(
                map((res: any) => {
                    const rows = Array.isArray(res?.data) ? res.data : [];
                    const scored = rows
                        .map((u: any) => ({
                            user: u,
                            type: t,
                            score: this.scoreUserMatch(
                                u,
                                query,
                                queryKind
                            ),
                        }))
                        .filter((x: { score: number }) => x.score > 0);
                    if (scored.length) {
                        return { denied: false as const, hits: scored };
                    }
                    return {
                        denied: false as const,
                        hits: rows.slice(0, 5).map((u: any) => ({
                            user: u,
                            type: t,
                            score: 35,
                        })),
                    };
                }),
                catchError((err) =>
                    of({
                        denied: err?.status === 403 || err?.status === 401,
                        hits: [] as Array<{
                            user: any;
                            type: SlateUserListType;
                            score: number;
                        }>,
                    })
                )
            );
        });

        return forkJoin(calls).pipe(
            map((groups) => {
                const anyDenied = groups.some((g) => g.denied);
                const hits = groups
                    .flatMap((g) => g.hits)
                    .sort(
                        (a, b) =>
                            b.score - a.score ||
                            this.formatUserDisplayName(a.user).localeCompare(
                                this.formatUserDisplayName(b.user)
                            )
                    );

                if (!hits.length) {
                    if (anyDenied) {
                        return [
                            {
                                type: 'refuse' as const,
                                title: 'Access unavailable',
                                body: 'Studio denied access to that user directory (permission missing or blocked). Ask a Super Admin to review your role, or try Help and support.',
                                chips: [
                                    'Help and support',
                                    'How do I open the dashboard?',
                                ],
                                links: [
                                    {
                                        label: 'Open Access Control',
                                        route: '/admin/settings/access-control',
                                    },
                                    ...this.supportLinks(),
                                ],
                            },
                        ];
                    }
                    return [
                        {
                            type: 'clarify' as const,
                            title: 'No match',
                            body: `I could not find a Studio user matching “${query}”. Try another number, email, or name — or open the user list to browse.`,
                            chips: [
                                'List viewers',
                                'List filmmakers',
                                'List content curators',
                                'List channel partners',
                            ],
                        },
                    ];
                }

                // Deduplicate by id+type
                const seen = new Set<string>();
                const unique = hits.filter((h) => {
                    const id = String(h.user._id || h.user.id || '');
                    const key = `${h.type}:${id}`;
                    if (!id || seen.has(key)) return false;
                    seen.add(key);
                    return true;
                });

                if (unique.length > 1) {
                    const nameKeys = new Set(
                        unique.map((h) =>
                            this.formatUserDisplayName(h.user)
                                .toLowerCase()
                                .trim()
                        )
                    );
                    const phoneKeys = new Set(
                        unique.map((h) =>
                            normalizePhoneDigits(
                                `${h.user.countryCodeNumeric || ''}${h.user.phoneNumber || h.user.phone || ''}`
                            )
                        )
                    );
                    const samePerson =
                        nameKeys.size === 1 ||
                        (queryKind === 'phone' &&
                            [...phoneKeys].every(
                                (p) =>
                                    p &&
                                    (p.endsWith(query) ||
                                        query.endsWith(p) ||
                                        p.includes(query))
                            ));

                    if (samePerson) {
                        return [
                            this.buildMultiRoleUserCard(
                                unique.slice(0, 5),
                                openMode
                            ),
                        ];
                    }

                    return [
                        {
                            type: 'clarify' as const,
                            title: 'Which user?',
                            body: `Several users match “${query}”. Pick one:`,
                            chips: unique.slice(0, 5).map((h) => {
                                const meta = this.userTypeMeta(h.type);
                                const name = this.formatUserDisplayName(
                                    h.user
                                );
                                return `Open ${meta.chipNoun} "${name}"`;
                            }),
                        },
                    ];
                }

                return [
                    this.buildSingleUserCard(unique[0], openMode, open),
                ];
            })
        );
    }

    private buildSingleUserCard(
        top: { user: any; type: string; score: number },
        openMode: 'view' | 'edit',
        open: boolean
    ): SlateResponseCard {
        const meta = this.userTypeMeta(top.type as SlateUserListType);
        const id = String(top.user._id || top.user.id);
        const name = this.formatUserDisplayName(top.user);
        const phone = this.formatUserPhone(top.user);
        const email = top.user.email || '—';
        const status = this.formatUserAccountStatus(top.user);
        const profile = meta.profileRoute(id, openMode);

        const kpis: SlateKpiItem[] = [
            { label: 'Role', value: meta.label },
            { label: 'Name', value: name, copyable: true },
            {
                label: 'Phone',
                value: phone,
                copyable: phone !== '—',
            },
            {
                label: 'Email',
                value: email,
                copyable: email !== '—',
            },
        ];
        if (status && status !== '—') {
            kpis.push({ label: 'Status', value: status });
        }

        return {
            type: 'kpi',
            title: `${meta.label} found`,
            live: true,
            body: open
                ? `Matched in Studio. Use the button below to open their profile in ${openMode} mode.`
                : `${name} · ${phone} · ${email}`,
            kpis,
            links: [
                {
                    label:
                        openMode === 'edit'
                            ? 'Open profile (edit)'
                            : 'Open profile',
                    route: profile.route,
                    queryParams: profile.queryParams,
                },
                {
                    label: 'Copy profile link',
                    route: profile.route,
                    queryParams: profile.queryParams,
                    copyText: this.absoluteStudioUrl(
                        profile.route,
                        profile.queryParams
                    ),
                },
                {
                    label: `All ${meta.label.toLowerCase()}s`,
                    route: meta.listRoute,
                },
            ],
            chips: [
                openMode === 'view'
                    ? `Open ${meta.chipNoun} "${name}" in edit mode`
                    : `Open ${meta.chipNoun} "${name}"`,
                `List ${meta.chipNoun}s`,
            ],
        };
    }

    private buildMultiRoleUserCard(
        hits: Array<{ user: any; type: SlateUserListType; score: number }>,
        openMode: 'view' | 'edit'
    ): SlateResponseCard {
        const primary = hits[0].user;
        const name = this.formatUserDisplayName(primary);
        const phone = this.formatUserPhone(primary);
        const email =
            hits.map((h) => h.user.email).find((e) => !!e) || '—';
        const roles = hits.map((h) => this.userTypeMeta(h.type).label);

        const kpis: SlateKpiItem[] = [
            { label: 'Name', value: name, copyable: true },
            {
                label: 'Phone',
                value: phone,
                copyable: phone !== '—',
            },
            {
                label: 'Email',
                value: email,
                copyable: email !== '—',
            },
            { label: 'Roles', value: roles.join(' · ') },
        ];

        const links: SlateResponseCard['links'] = [];
        for (const h of hits) {
            const meta = this.userTypeMeta(h.type);
            const id = String(h.user._id || h.user.id);
            const profile = meta.profileRoute(id, openMode);
            links.push({
                label: `Open ${meta.chipNoun}`,
                route: profile.route,
                queryParams: profile.queryParams,
            });
            links.push({
                label: `Copy ${meta.chipNoun} link`,
                route: profile.route,
                queryParams: profile.queryParams,
                copyText: this.absoluteStudioUrl(
                    profile.route,
                    profile.queryParams
                ),
            });
        }

        return {
            type: 'kpi',
            title: 'User found · multiple roles',
            live: true,
            body: `Same contact appears as ${roles.join(' and ')} in Studio.`,
            kpis,
            links,
            chips: hits.map((h) => {
                const meta = this.userTypeMeta(h.type);
                return `Open ${meta.chipNoun} "${this.formatUserDisplayName(h.user)}"`;
            }),
        };
    }

    private scoreUserMatch(
        u: any,
        query: string,
        queryKind: 'phone' | 'email' | 'name'
    ): number {
        if (queryKind === 'phone') {
            const q = normalizePhoneDigits(query);
            const phone = normalizePhoneDigits(
                `${u.countryCodeNumeric || ''}${u.phoneNumber || u.phone || ''}`
            );
            const phoneOnly = normalizePhoneDigits(
                u.phoneNumber || u.phone || ''
            );
            if (!q) return 0;
            if (phone === q || phoneOnly === q) return 100;
            if (phone.endsWith(q) || q.endsWith(phoneOnly)) return 90;
            if (phone.includes(q) || phoneOnly.includes(q)) return 70;
            // Backend may have returned row via matchParams without phone field match
            return 0;
        }
        if (queryKind === 'email') {
            const email = String(u.email || '').toLowerCase();
            const q = query.toLowerCase();
            if (email === q) return 100;
            if (email.includes(q)) return 80;
            return 0;
        }
        const name = this.formatUserDisplayName(u).toLowerCase();
        const q = query.toLowerCase().trim();
        if (!q || name === '—') return 0;
        if (name === q) return 100;
        if (name.startsWith(q)) return 85;
        if (name.includes(q)) return 65;
        const tokens = q.split(/\s+/).filter(Boolean);
        const hits = tokens.filter((tok) => name.includes(tok)).length;
        if (hits) return Math.min(55, (hits / tokens.length) * 55);
        return 0;
    }

    private buildFilmDetailCard(
        film: any,
        focuses: NonNullable<SlateEntities['detailFocus']>,
        kind: 'film' | 'webseries' = 'film'
    ): SlateResponseCard {
        const title = film.title || film.filmName || 'Untitled';
        const id = String(film._id || film.id || '');
        const wantSummary = focuses.includes('summary');
        // Strict: only fields user asked — never treat summary as "all metrics"
        const want = (f: string) =>
            wantSummary
                ? f === 'filmmaker' ||
                  f === 'status' ||
                  f === 'purchases' ||
                  f === 'views' ||
                  f === 'revenue' ||
                  f === 'price'
                : focuses.includes(f as any);

        const seasons = Array.isArray(film.seasons) ? film.seasons : [];
        const status =
            film.status ||
            film.latestSeasonStatus ||
            seasons[0]?.status ||
            '—';
        const purchases =
            film.purchaseCount ??
            film.purchasedCount ??
            film.purchase ??
            (seasons.length
                ? seasons.reduce(
                      (s: number, x: any) =>
                          s + (Number(x.purchaseCount) || 0),
                      0
                  )
                : null);
        const views =
            film.views ??
            film.totalView ??
            (seasons.length
                ? seasons.reduce(
                      (s: number, x: any) => s + (Number(x.views) || 0),
                      0
                  )
                : null);
        const revenue =
            film.revenue ??
            film.totalEarnings ??
            film.filmmakerRevenue ??
            null;
        const price =
            film.price?.totalPrice ??
            film.totalPrice ??
            film.lastSeasonPrice?.totalPrice ??
            film.priceUSD?.totalPrice ??
            null;

        const kpis: SlateKpiItem[] = [];
        const lines: string[] = [];

        if (want('filmmaker')) {
            const contact = this.formatFilmmakerContact(film);
            kpis.push({
                label: 'Name',
                value: contact.name,
                copyable: true,
            });
            kpis.push({
                label: 'Email',
                value: contact.email,
                copyable: contact.email !== '—',
            });
            kpis.push({
                label: 'Mobile',
                value: contact.phone,
                copyable: contact.phone !== '—',
            });
            lines.push(
                `Filmmaker: ${contact.name}` +
                    (contact.email !== '—'
                        ? ` · ${contact.email}`
                        : '') +
                    (contact.phone !== '—'
                        ? ` · ${contact.phone}`
                        : '')
            );
        }
        if (want('status')) {
            kpis.push({ label: 'Status', value: status });
            lines.push(`Status: ${status}`);
        }
        if (want('purchases')) {
            const v = purchases ?? '—';
            kpis.push({ label: 'Purchases', value: v });
            lines.push(`Purchases: ${v}`);
        }
        if (want('views')) {
            const v = views ?? '—';
            kpis.push({ label: 'Views', value: v });
            lines.push(`Views: ${v}`);
        }
        if (want('revenue')) {
            const v = revenue ?? '—';
            kpis.push({ label: 'Revenue', value: v });
            lines.push(`Revenue: ${v}`);
        }
        if (want('price')) {
            const v = price ?? '—';
            kpis.push({ label: 'Price', value: v });
            lines.push(`Ticket price: ${v}`);
        }

        const kindLabel = kind === 'webseries' ? 'Web series' : 'Film';
        const focusLabel = wantSummary
            ? `${kindLabel} details`
            : focuses
                  .map((f) =>
                      f === 'filmmaker'
                          ? 'Filmmaker'
                          : f.charAt(0).toUpperCase() + f.slice(1)
                  )
                  .join(' · ');

        const noun = kind === 'webseries' ? 'series' : 'film';
        const chips: string[] = [];
        // Soft follow-ups only — do not dump every metric chip on a focused ask
        if (wantSummary) {
            chips.push(
                `Who is the filmmaker of "${title}" ${noun}?`,
                `What is the status of "${title}" ${noun}?`,
                `How many purchases for "${title}" ${noun}?`
            );
        } else if (want('filmmaker')) {
            chips.push(`Tell me about "${title}" ${noun}`);
            chips.push(`What is the status of "${title}" ${noun}?`);
        } else if (want('status')) {
            chips.push(`Who is the filmmaker of "${title}" ${noun}?`);
            chips.push(`Tell me about "${title}" ${noun}`);
        } else {
            chips.push(`Tell me about "${title}" ${noun}`);
        }

        const route = this.isFilmmaker()
            ? kind === 'webseries'
                ? `/film-maker/my-series/${id}`
                : `/film-maker/my-films/${id}`
            : kind === 'webseries'
              ? `/admin/box-office/webseries/view/${id}`
              : `/admin/box-office/films/view/${id}`;

        const links: SlateResponseCard['links'] = [
            {
                label:
                    kind === 'webseries' ? 'Open web series' : 'Open film',
                route,
            },
        ];
        const fmId =
            film.filmMakerId ||
            film.filmMaker?._id ||
            film.filmMaker?.id;
        if (!this.isFilmmaker() && fmId && want('filmmaker')) {
            const fmRoute = `/admin/users/film-makers/${fmId}`;
            const fmQuery = { isEdit: 'false' };
            links.push({
                label: 'Open filmmaker',
                route: fmRoute,
                queryParams: fmQuery,
            });
            links.push({
                label: 'Copy profile link',
                route: fmRoute,
                queryParams: fmQuery,
                copyText: this.absoluteStudioUrl(fmRoute, fmQuery),
            });
        }

        const card: SlateResponseCard = {
            type: 'kpi',
            title: `${focusLabel} · ${title}`,
            live: true,
            body: lines.join(' · ') || undefined,
            shortBody: lines[0],
            kpis,
            chips: withHelpChips(chips.slice(0, 4)),
            links,
        };

        // Series depth: season count when payload has seasons
        if (kind === 'webseries' && wantSummary && seasons.length) {
            const episodeCount = seasons.reduce(
                (n: number, s: any) =>
                    n +
                    (Array.isArray(s.episodes) ? s.episodes.length : 0),
                0
            );
            return {
                ...card,
                body:
                    (card.body ? card.body + ' · ' : '') +
                    `${seasons.length} season(s)` +
                    (episodeCount ? ` · ${episodeCount} episode(s)` : ''),
                chips: withHelpChips([
                    ...chips.slice(0, 3),
                    'Top 5 seasons',
                ]),
            };
        }

        return card;
    }

    private formatFilmmakerName(film: any): string {
        return this.formatFilmmakerContact(film).name;
    }

    private formatFilmmakerContact(film: any): {
        name: string;
        email: string;
        phone: string;
    } {
        if (film.filmMakerName && !film.filmMaker) {
            return {
                name: String(film.filmMakerName),
                email: '—',
                phone: '—',
            };
        }
        const fm = film.filmMaker;
        if (!fm) {
            return {
                name: film.filmMakerName
                    ? String(film.filmMakerName)
                    : '—',
                email: '—',
                phone: '—',
            };
        }
        if (typeof fm === 'string') {
            return { name: fm, email: '—', phone: '—' };
        }
        let name = '';
        if (fm.name) name = String(fm.name);
        else {
            const first = fm.firstname || fm.firstName || '';
            const last = fm.lastname || fm.lastName || '';
            name = `${first} ${last}`.trim();
        }
        if (!name && fm.productionHouse) {
            name = String(fm.productionHouse);
        }
        if (!name && film.filmMakerName) {
            name = String(film.filmMakerName);
        }
        const email = fm.email ? String(fm.email) : '—';
        const phone = this.formatPhoneDisplay(
            fm.phoneNumber || fm.phone || '',
            fm.countryCodeNumeric || fm.countryCode || ''
        );
        return { name: name || '—', email, phone };
    }

    private absoluteStudioUrl(
        route: string,
        queryParams?: Record<string, string>
    ): string {
        const origin =
            typeof window !== 'undefined' && window.location?.origin
                ? window.location.origin
                : '';
        const q =
            queryParams && Object.keys(queryParams).length
                ? `?${new URLSearchParams(queryParams).toString()}`
                : '';
        return `${origin}${route}${q}`;
    }

    private searchFilmsRich(
        query: string
    ): Observable<Array<Record<string, any>>> {
        if (this.isFilmmaker()) {
            return this._filmMaker
                .getFilmList({
                    page: 1,
                    limit: 50,
                    matchParams: query,
                })
                .pipe(
                    map((res: any) => {
                        const rows = Array.isArray(res?.data)
                            ? res.data
                            : [];
                        return rows.map((f: any) => ({
                            ...f,
                            _id: f._id || f.id,
                            title: f.title || f.filmName || '',
                        }));
                    }),
                    catchError((err) =>
                        err?.status === 403 || err?.status === 401
                            ? throwError(() => err)
                            : of([])
                    )
                );
        }
        return this.withPageContext(RbacPage.FILM_LIST, () =>
            this._adminFilm.getFilmList({
                page: 1,
                limit: 50,
                matchParams: query,
            })
        ).pipe(
            map((res: any) => {
                const rows = Array.isArray(res?.data) ? res.data : [];
                return rows.map((f: any) => ({
                    ...f,
                    _id: f._id || f.id,
                    title: f.title || f.filmName || '',
                }));
            }),
            catchError((err) =>
                err?.status === 403 || err?.status === 401
                    ? throwError(() => err)
                    : of([])
            )
        );
    }

    private searchSeriesRich(
        query: string
    ): Observable<Array<Record<string, any>>> {
        if (this.isFilmmaker()) {
            return this._filmMaker
                .getWebseriesList({
                    page: 1,
                    limit: 50,
                    matchParams: query,
                })
                .pipe(
                    map((res: any) => {
                        const rows = Array.isArray(res?.data)
                            ? res.data
                            : [];
                        return rows.map((s: any) => ({
                            ...s,
                            _id: s._id || s.id,
                            title: s.title || '',
                            status: s.latestSeasonStatus || s.status,
                        }));
                    }),
                    catchError((err) =>
                        err?.status === 403 || err?.status === 401
                            ? throwError(() => err)
                            : of([])
                    )
                );
        }
        return this.withPageContext(RbacPage.WEB_SERIES_LIST, () =>
            this._adminFilm.getWebSeriesList({
                page: 1,
                limit: 50,
                matchParams: query,
            })
        ).pipe(
            map((res: any) => {
                const rows = Array.isArray(res?.data) ? res.data : [];
                return rows.map((s: any) => ({
                    ...s,
                    _id: s._id || s.id,
                    title: s.title || '',
                    status: s.latestSeasonStatus || s.status,
                }));
            }),
            catchError((err) =>
                err?.status === 403 || err?.status === 401
                    ? throwError(() => err)
                    : of([])
            )
        );
    }

    private openContent(e: SlateEntities): Observable<SlateResponseCard[]> {
        const mode = e.openMode || 'view';
        let kind = e.contentKind || 'unknown';
        const contentId = String(e.filmId || e.seriesId || '').trim();
        const title = (e.filmTitleQuery || e.filmTitle || '').trim();

        if (contentId) {
            return this.openContentById(kind, mode, contentId, title);
        }

        if (!title) {
            return of([
                {
                    type: 'clarify',
                    title: 'Which title?',
                    body: 'Tell me the film or series name, for example: open "z33" film in view mode.',
                    chips: [
                        'List films from last 30 days',
                        'List web series',
                    ],
                },
            ]);
        }

        const search$ =
            kind === 'webseries'
                ? this.searchSeriesByTitle(title).pipe(
                      map((rows) => ({ kind: 'webseries' as const, rows }))
                  )
                : kind === 'film'
                  ? this.searchFilmsByTitle(title).pipe(
                        map((rows) => ({ kind: 'film' as const, rows }))
                    )
                  : this.searchFilmsByTitle(title).pipe(
                        switchMap((films) => {
                            if (films.length) {
                                return of({
                                    kind: 'film' as const,
                                    rows: films,
                                });
                            }
                            return this.searchSeriesByTitle(title).pipe(
                                map((rows) => ({
                                    kind: 'webseries' as const,
                                    rows,
                                }))
                            );
                        })
                    );

        return search$.pipe(
            map(({ kind: resolvedKind, rows }) => {
                kind = resolvedKind;
                const ranked = rankFilmMatches(title, rows);
                const pick = resolveFilmPick(ranked, { strict: true });

                if (pick.kind === 'none') {
                    return [this.notInYourCatalogCard(kind, title)];
                }

                if (pick.kind === 'ambiguous') {
                    const label =
                        kind === 'webseries' ? 'series' : 'film';
                    return [
                        {
                            type: 'clarify' as const,
                            title: 'Which title?',
                            body: `Several of your ${label} titles match “${title}”. Pick one:`,
                            chips: pick.films.map((f) =>
                                this.openChipLabel(kind, mode, f)
                            ),
                        },
                    ];
                }

                return this.buildOpenGuideCards(
                    kind,
                    mode,
                    pick.film.id,
                    pick.film.title
                );
            })
        );
    }

    private openContentById(
        kind: 'film' | 'webseries' | 'unknown',
        mode: 'view' | 'edit',
        id: string,
        fallbackTitle: string
    ): Observable<SlateResponseCard[]> {
        const access = this.checkOpenAccess(kind, mode);
        if (!access.ok) {
            return of([
                {
                    type: 'refuse' as const,
                    title: 'Access unavailable',
                    body: access.message,
                    chips: this.isFilmmaker()
                        ? ['Show my films', 'How do I upload a film?']
                        : [
                              'List films from last 30 days',
                              'How do I upload a film?',
                          ],
                    links: access.helpLink ? [access.helpLink] : undefined,
                },
            ]);
        }

        if (this.isFilmmaker() && kind !== 'webseries') {
            return this._filmMaker.getFilmById(id).pipe(
                map((res: any) => {
                    const film = res?.data || res;
                    const filmId = String(film?._id || film?.id || '');
                    if (!filmId) {
                        return [
                            this.notInYourCatalogCard(
                                'film',
                                fallbackTitle || id
                            ),
                        ];
                    }
                    const filmTitle =
                        film?.title || film?.filmName || fallbackTitle || id;
                    return this.buildOpenGuideCards(
                        'film',
                        mode,
                        filmId,
                        filmTitle
                    );
                }),
                catchError(() =>
                    of([
                        this.notInYourCatalogCard(
                            'film',
                            fallbackTitle || id
                        ),
                    ])
                )
            );
        }

        if (this.isFilmmaker() && kind === 'webseries') {
            return this._filmMaker
                .getWebseriesList({
                    page: 1,
                    limit: 50,
                    matchParams: fallbackTitle || '',
                })
                .pipe(
                    map((res: any) => {
                        const rows = Array.isArray(res?.data) ? res.data : [];
                        const hit = rows.find(
                            (s: any) =>
                                String(s._id || s.id) === id
                        );
                        if (!hit) {
                            return [
                                this.notInYourCatalogCard(
                                    'webseries',
                                    fallbackTitle || id
                                ),
                            ];
                        }
                        return this.buildOpenGuideCards(
                            'webseries',
                            mode,
                            id,
                            hit.title || fallbackTitle || id
                        );
                    }),
                    catchError(() =>
                        of([
                            this.notInYourCatalogCard(
                                'webseries',
                                fallbackTitle || id
                            ),
                        ])
                    )
                );
        }

        const displayTitle = fallbackTitle || id;
        const resolvedKind =
            kind === 'unknown' ? ('film' as const) : kind;
        return of(
            this.buildOpenGuideCards(resolvedKind, mode, id, displayTitle)
        );
    }

    private buildOpenGuideCards(
        kind: 'film' | 'webseries' | 'unknown',
        mode: 'view' | 'edit',
        id: string,
        title: string
    ): SlateResponseCard[] {
        const access = this.checkOpenAccess(kind, mode);
        if (!access.ok) {
            return [
                {
                    type: 'refuse' as const,
                    title: 'Access unavailable',
                    body: access.message,
                    chips: this.isFilmmaker()
                        ? ['Show my films', 'How do I upload a film?']
                        : [
                              'List films from last 30 days',
                              'How do I upload a film?',
                          ],
                    links: access.helpLink ? [access.helpLink] : undefined,
                },
            ];
        }

        const route = this.buildOpenRoute(kind, mode, id);
        const modeLabel = mode === 'edit' ? 'edit' : 'view';
        const kindLabel = kind === 'webseries' ? 'web series' : 'film';

        return [
            {
                type: 'guide' as const,
                title: `Opening “${title}”`,
                body: `Ready in ${modeLabel} mode for this ${kindLabel}.`,
                steps: [
                    `Matched: ${title}`,
                    `Mode: ${modeLabel}`,
                    'Use the button below to open the page.',
                ],
                links: [
                    {
                        label:
                            mode === 'edit'
                                ? 'Open in edit mode'
                                : 'Open in view mode',
                        route,
                    },
                ],
                chips: [
                    mode === 'view'
                        ? this.openChipLabel(kind, 'edit', { id, title })
                        : this.openChipLabel(kind, 'view', { id, title }),
                ],
            },
        ];
    }

    private checkOpenAccess(
        kind: 'film' | 'webseries' | 'unknown',
        mode: 'view' | 'edit'
    ): {
        ok: boolean;
        message: string;
        helpLink?: { label: string; route: string };
    } {
        if (this.isFilmmaker()) {
            return { ok: true, message: '' };
        }

        if (!this._rbac.isLoaded()) {
            return { ok: true, message: '' };
        }

        const page =
            kind === 'webseries'
                ? mode === 'edit'
                    ? RbacPage.WEB_SERIES_EDIT
                    : RbacPage.WEB_SERIES_VIEW
                : mode === 'edit'
                  ? RbacPage.FILM_EDIT
                  : RbacPage.FILM_VIEW;

        const allowed =
            mode === 'edit'
                ? this._rbac.can(page, 'update')
                : this._rbac.can(page, 'read');

        if (allowed) {
            return { ok: true, message: '' };
        }

        const helpLink = {
            label: 'Open Access Control',
            route: '/admin/settings/access-control',
        };

        if (mode === 'edit') {
            return {
                ok: false,
                message:
                    'You do not currently have access to open this title in edit mode. Your account can still use other Studio features — if you need edit rights, please reach out to a Super Admin and they can update your role.',
                helpLink,
            };
        }

        return {
            ok: false,
            message:
                'You do not currently have access to open this title in view mode. If that seems unexpected, please contact a Super Admin so they can review your role permissions.',
            helpLink,
        };
    }

    private buildOpenRoute(
        kind: 'film' | 'webseries' | 'unknown',
        mode: 'view' | 'edit',
        id: string
    ): string {
        if (this.isFilmmaker()) {
            if (kind === 'webseries') {
                return `/film-maker/my-series/${id}`;
            }
            return mode === 'edit'
                ? `/film-maker/media-upload/${id}`
                : `/film-maker/my-films/${id}`;
        }
        if (kind === 'webseries') {
            return mode === 'edit'
                ? `/admin/box-office/webseries/edit/${id}`
                : `/admin/box-office/webseries/view/${id}`;
        }
        return mode === 'edit'
            ? `/admin/box-office/films/edit/${id}`
            : `/admin/box-office/films/view/${id}`;
    }

    private searchSeriesByTitle(
        query: string
    ): Observable<Array<{ _id: string; title: string }>> {
        if (this.isFilmmaker()) {
            return this._filmMaker
                .getWebseriesList({
                    page: 1,
                    limit: 50,
                    matchParams: query,
                })
                .pipe(
                    map((res: any) => {
                        const rows = Array.isArray(res?.data)
                            ? res.data
                            : [];
                        return rows.map((s: any) => ({
                            _id: s._id || s.id,
                            title: s.title || s.name || '',
                        }));
                    }),
                    catchError(() => of([]))
                );
        }
        return this.withPageContext(RbacPage.WEB_SERIES_LIST, () =>
            this._adminFilm.getSimpleWebSeriesList(1, 50, undefined, query)
        ).pipe(
            map((res: any) => {
                const rows = Array.isArray(res?.data?.webSeries)
                    ? res.data.webSeries
                    : Array.isArray(res?.data)
                      ? res.data
                      : [];
                return rows.map((s: any) => ({
                    _id: s._id || s.id,
                    title: s.title || '',
                }));
            }),
            catchError(() => of([]))
        );
    }

}
